import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Apiservice } from '../../../../services/ApiService';
import { AuthService } from '../../../../services/AuthService';
import { VenueService } from '../../../../services/VenueService';
import { PermissionsService } from '../../../../services/Permissions';
import { ExcelService, StyledCell } from '../../../../services/ExcelService';
import {
  ScanTestRecord,
  ScanTestBarcode,
  ScanTestMasterConfig,
  CurrentRun,
  GateGroup,
  GateRule,
  FlatGateRule,
  LegacyGateRule,
  EntryRule,
  AccessGroup,
  RuleItemRef,
  buildScanTestKey,
  buildDefaultGateArray,
  migrateGateRule,
  liftFlatGateRule,
  isMasterConfig
} from '../../../../models/scan-testing.model';
import { EventScanMeta, Performance, PerformanceAuditTrailEntry, PerformanceCreateRequest, PerformanceExpectedChange, PerformanceStatus, PerformanceStatusHistoryEntry, SCANNER_MESSAGE_MAX_LENGTH, TicketUsage } from '../../../../models/performance.model';
import { AdvanceStageDescriptor, AdvanceStageResult } from '../advance-stage/advance-stage.component';
import { CopyTarget, CopyTargetsRequest } from '../gate-groups/gate-groups.component';
import { RuleEvent, RuleSavePayload, SeedRowRequest } from '../set-rules/set-rules.component';

type EventType = 'archtics' | 'host';

interface BarcodeRow {
  item: number;
  database: string;
  plan: string;
  description: string;
  barcode: string;
  priceCode: string;
  section: string;
  row: string;
  seat: string;
  usageLabel: string;
  numSeats: string;
  extended: boolean;
  eventType: EventType;
  raw: ScanTestBarcode;
  editingBarcode: boolean;
  barcodeDraft: string;
  barcodeError: boolean;
}

interface DraftRow {
  database: string;
  plan: string;
  description: string;
  barcode: string;
  priceCode: string;
  section: string;
  row: string;
  seat: string;
}

interface EventDraft {
  eventName: string;
  description: string;
  database: string;
  eventType: EventType;
  errors: { eventName: boolean; database: boolean; duplicate: boolean };
}

interface AuditEntry {
  time: number;
  username: string;
  action: 'create' | 'update' | 'test_added';
  summary: string;
  details: string[];
  variant?: 'rollback';
}

export interface MatrixColumn {
  kind: 'group' | 'gate';
  id: string;
  label: string;
  gates: string[];
}

// A single event's KEEP/REMOVE transition parsed from an uploaded listing.
interface ImportChange {
  eventName: string;
  from: 'KEEP' | 'REMOVE';
  to: 'KEEP' | 'REMOVE';
}

// Result of parsing + validating an uploaded event listing against the
// events currently loaded on the page.
interface ImportPreview {
  fileName: string;
  changes: ImportChange[];
  unchangedCount: number;
  unmatchedFileEvents: string[];
  missingFromFile: string[];
  conflicts: string[];
  nextHiddenEvents: string[];
}

interface EventViewModel {
  eventName: string;
  database: string;
  description: string;
  eventType: EventType;
  apiRows: BarcodeRow[];
  extendedRows: BarcodeRow[];
  expanded: boolean;
  matrixExpanded: boolean;
  keep: boolean;
  draft: DraftRow | null;
  draftErrors: { db: boolean; locator: boolean; barcode: boolean };
}

const STAGES: AdvanceStageDescriptor[] = [
  { index: 0, code: 'EL', label: 'Event listing', sub: 'loaded', status: 'event_listing_loaded' },
  { index: 1, code: 'SR', label: 'Set rules', sub: '—', activeLabel: 'Setting rules', status: 'rules_set' },
  { index: 2, code: 'TC', label: 'Set test', sub: 'config', activeLabel: 'Setting test config', status: 'test_config' },
  { index: 3, code: 'TM1', label: 'TM1 entry', sub: 'configured', status: 'client_confirmed' },
  { index: 4, code: 'TST', label: 'Testing', sub: '—', status: 'tm1_entry_configured' }
];

@Component({
  selector: 'app-performance-detail',
  templateUrl: './performance-detail.component.html',
  standalone: false,
  styleUrls: ['./performance-detail.component.scss', '../../styles/shared.scss']
})
export class PerformanceDetailComponent implements OnInit, OnDestroy {
  // Sentinel event name for the group that holds rule-seeded extended test rows.
  // These rows aren't tied to a real event, so they're gathered under one clearly
  // labelled group and excluded from the rule-authoring event palette.
  private static readonly SEED_EVENT_NAME = 'Rule-seeded tests';

  readonly stages = STAGES;
  activeStageIndex = 0;

  loading = false;
  loadError = '';
  saving = false;

  venueID = '';
  venueName = '';
  venueTimezone = '';
  performanceTimestamp = 0;
  performanceDate = '';
  performanceTitle = '';
  performanceSubtitle = '';
  scanTestKey = '';

  gateNames: string[] = [];
  events: EventViewModel[] = [];
  audit: AuditEntry[] = [];
  performanceDescription = '';
  performanceStatus: PerformanceStatus = STAGES[0].status;
  statusHistory: PerformanceStatusHistoryEntry[] = [];
  hiddenEventNames: string[] = [];
  customArchticsEventNames: string[] = [];
  customHostEventNames: string[] = [];
  // Per-event access-control metadata (scanner message + one-time/multi-day),
  // keyed by event code. Persisted on the performance record.
  eventScanMeta: Record<string, EventScanMeta> = {};
  readonly scannerMessageMaxLength = SCANNER_MESSAGE_MAX_LENGTH;
  currentUserEmail = '';
  keepSaving = false;
  metaSaving = false;

  showAdvanceModal = false;
  advanceSaving = false;
  advanceError = '';

  showAuditModal = false;

  showRowDetailModal = false;
  rowDetailRow: BarcodeRow | null = null;
  rowDetailEvent: EventViewModel | null = null;

  showBackModal = false;
  backSaving = false;
  backError = '';

  importPreview: ImportPreview | null = null;
  importParsing = false;
  importSaving = false;
  importError = '';

  tm1EditUnlocked = false;
  showTm1EditModal = false;
  tm1EditConfirmation = '';
  private static readonly TM1_EDIT_PHRASE = 'Make Edits';

  isTicketmaster = false;
  isGlobalAdmin = false;
  private permissionSubs: { unsubscribe(): void }[] = [];

  matrixOnlyExtended = false;
  matrixOnlyFailed = false;
  eventDraft: EventDraft | null = null;
  allDatabases: string[] = [];
  selectedDatabases = new Set<string>();
  selectedRowItems = new Set<number>();
  bulkSaving = false;

  gateGroups: GateGroup[] = [];
  gateGroupsView: 'grouped' | 'individual' = 'individual';
  showGateGroupsModal = false;
  gateGroupsSaving = false;
  gateGroupsError = '';
  expandedGroupIds = new Set<string>();
  private masterRecord: ScanTestMasterConfig | null = null;

  scanRules: GateRule[] = [];
  rulesSaving = false;
  rulesError = '';
  // Stable palette for the Set Rules editor. Recomputed only when the event VM
  // or keep flags change — never rebuilt inside a template binding.
  ruleEventsList: RuleEvent[] = [];

  // Reusable access groups (named event/plan bundles), persisted on the master.
  accessGroups: AccessGroup[] = [];
  accessGroupsSaving = false;
  accessGroupsError = '';

  // Price-code suggestions offered to the Price Code rule modal, derived from the
  // distinct price codes seen on the loaded barcodes.
  availablePriceCodes: string[] = [];

  copyTargets: CopyTarget[] = [];
  copyLoading = false;
  copyRunning = false;
  copyError = '';
  copyDone = false;
  testRunning = false;
  testRunningRow: number | null = null;
  testRunError = '';
  currentRun: CurrentRun | null = null;
  runQueue: number[] = [];
  runQueueTotal = 0;

  markReadySaving = false;
  markReadyError = '';
  private pollHandle: ReturnType<typeof setTimeout> | null = null;
  private static readonly POLL_INTERVAL_MS = 2500;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiservice: Apiservice,
    private auth: AuthService,
    private venueService: VenueService,
    private permissions: PermissionsService,
    private excelService: ExcelService
  ) {}

  ngOnInit(): void {
    const venueParam = this.route.snapshot.paramMap.get('venueID') ?? '';
    const tsParam = this.route.snapshot.paramMap.get('timestamp') ?? '';
    this.venueID = venueParam;
    this.performanceTimestamp = Number(tsParam);
    if (!this.venueID || !this.performanceTimestamp) {
      this.loadError = 'Invalid performance link.';
      return;
    }

    this.auth.getCurrentUser()
      .then((user) => {
        this.currentUserEmail = (user as any)?.signInDetails?.loginId ?? (user as any)?.username ?? '';
      })
      .catch(() => { this.currentUserEmail = ''; });

    this.permissionSubs.push(
      this.permissions.ticketmaster.subscribe((val) => { this.isTicketmaster = !!val; }),
      this.permissions.globalAdmin.subscribe((val) => { this.isGlobalAdmin = !!val; })
    );

    this.loading = true;
    this.venueService.getVenueList().subscribe({
      next: (venues: any[]) => {
        const match = venues.find((v) => v.key === this.venueID);
        this.venueName = match?.value?.name ?? this.venueID;
        this.venueTimezone = match?.value?.timezone ?? 'Australia/Sydney';
        this.performanceDate = this.epochToVenueDate(this.performanceTimestamp);
        this.scanTestKey = buildScanTestKey(this.venueID, this.performanceTimestamp);
        this.applyTitle();
        this.loadStageOneData();
      },
      error: () => {
        this.loadError = 'Failed to resolve venue.';
        this.loading = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.permissionSubs.forEach((s) => s.unsubscribe());
    this.permissionSubs = [];
  }

  goBack() {
    this.router.navigate(['/performance']);
  }

  openAuditModal() {
    this.showAuditModal = true;
  }

  closeAuditModal() {
    this.showAuditModal = false;
  }

  openRowDetailModal(ev: EventViewModel, row: BarcodeRow) {
    if (this.currentStage.code !== 'TST') { return; }
    if (this.notScanning(row)) { return; }
    this.rowDetailRow = row;
    this.rowDetailEvent = ev;
    this.showRowDetailModal = true;
  }

  closeRowDetailModal() {
    this.showRowDetailModal = false;
    this.rowDetailRow = null;
    this.rowDetailEvent = null;
  }

  cellLastTime(row: BarcodeRow, gate: string): number | null {
    const entry = row.raw.gateArray?.[gate];
    const history = entry?.resultHistory ?? [];
    if (!history.length) { return null; }
    const latest = history.reduce((a, b) => (Number(b.testTime) > Number(a.testTime) ? b : a));
    const t = Number(latest?.testTime);
    return t > 0 ? t : null;
  }

  downloadEventListing() {
    const events = this.visibleEventsForListing();
    if (!events.length) { return; }

    const rows: Record<string, string>[] = [];
    for (const ev of events) {
      const status = ev.keep ? 'KEEP' : 'REMOVE';
      const allRows = [...this.visibleApiRows(ev), ...this.visibleExtendedRows(ev)];

      if (!allRows.length) {
        rows.push({
          'Status': status,
          'Event': ev.eventName,
          'Description': ev.description || '',
          'Database': ev.database || '',
          'Type': '',
          'DB': '',
          'Plan': '',
          'Row Description': '',
          'Barcode': '',
          'Price Code': '',
          'Section': '',
          'Row': '',
          'Seat': '',
          'Not Scanning': ''
        });
        continue;
      }

      for (const r of allRows) {
        rows.push({
          'Status': status,
          'Event': ev.eventName,
          'Description': ev.description || '',
          'Database': ev.database || '',
          'Type': this.rowTypeLabel(r),
          'DB': r.database,
          'Plan': r.plan,
          'Row Description': r.description,
          'Barcode': r.barcode,
          'Price Code': r.priceCode,
          'Section': r.section,
          'Row': r.row,
          'Seat': r.seat,
          'Ticket Usage': this.usageDisplay(r),
          'Not Scanning': this.notScanning(r) ? 'Yes' : ''
        });
      }
    }

    this.excelService.exportAsExcelFile(rows, this.exportFilename('event_listing'));
  }

  // Fired by the hidden file input behind the "Import" button. Parses the
  // uploaded listing and opens a preview modal; nothing is saved until the
  // user confirms.
  async onImportFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Reset so re-selecting the same file fires the change event again.
    input.value = '';
    if (!file) { return; }

    this.importError = '';
    this.importParsing = true;
    try {
      const rows = await this.excelService.readAsExcelFile(file);
      this.importPreview = this.buildImportPreview(file.name, rows);
    } catch (err) {
      this.importError = err instanceof Error ? err.message : 'The file could not be read.';
    } finally {
      this.importParsing = false;
    }
  }

  // Diffs the uploaded Status column against the current KEEP/REMOVE state,
  // matching on event name. Conflicting Status values within one event are
  // flagged as errors; unmatched events on either side become warnings.
  private buildImportPreview(fileName: string, rows: Record<string, string>[]): ImportPreview {
    const statusByEvent = this.collectStatusByEvent(rows);
    const currentByName = new Map(this.events.map((ev) => [ev.eventName, ev]));

    const changes: ImportChange[] = [];
    const conflicts: string[] = [];
    const unmatchedFileEvents: string[] = [];
    const missingFromFile: string[] = [];
    let unchangedCount = 0;

    for (const [eventName, statuses] of statusByEvent) {
      const current = currentByName.get(eventName);
      if (!current) {
        unmatchedFileEvents.push(eventName);
        continue;
      }
      if (statuses.size > 1) {
        conflicts.push(eventName);
        continue;
      }
      const to = statuses.has('REMOVE') ? 'REMOVE' : 'KEEP';
      const from = current.keep ? 'KEEP' : 'REMOVE';
      if (from === to) {
        unchangedCount++;
      } else {
        changes.push({ eventName, from, to });
      }
    }

    for (const ev of this.events) {
      if (!statusByEvent.has(ev.eventName)) {
        missingFromFile.push(ev.eventName);
      }
    }

    return {
      fileName,
      changes,
      unchangedCount,
      unmatchedFileEvents,
      missingFromFile,
      conflicts,
      nextHiddenEvents: this.computeNextHiddenEvents(changes)
    };
  }

  // Groups the file's rows by event name, gathering the distinct normalised
  // Status values seen for each. Rows without a recognised Status are skipped.
  private collectStatusByEvent(rows: Record<string, string>[]): Map<string, Set<'KEEP' | 'REMOVE'>> {
    const byEvent = new Map<string, Set<'KEEP' | 'REMOVE'>>();
    for (const row of rows) {
      const eventName = (row['Event'] ?? '').trim();
      if (!eventName) { continue; }
      const status = this.normaliseStatus(row['Status']);
      if (!status) { continue; }
      const seen = byEvent.get(eventName) ?? new Set<'KEEP' | 'REMOVE'>();
      seen.add(status);
      byEvent.set(eventName, seen);
    }
    return byEvent;
  }

  private normaliseStatus(value: string | undefined): 'KEEP' | 'REMOVE' | null {
    const v = (value ?? '').trim().toUpperCase();
    if (v === 'KEEP') { return 'KEEP'; }
    if (v === 'REMOVE') { return 'REMOVE'; }
    return null;
  }

  // Applies the confirmed changes on top of the current hidden-event list:
  // REMOVE adds the name, KEEP drops it.
  private computeNextHiddenEvents(changes: ImportChange[]): string[] {
    const hidden = new Set(this.hiddenEventNames);
    for (const change of changes) {
      if (change.to === 'REMOVE') {
        hidden.add(change.eventName);
      } else {
        hidden.delete(change.eventName);
      }
    }
    return Array.from(hidden);
  }

  importHasConflicts(): boolean {
    return !!this.importPreview && this.importPreview.conflicts.length > 0;
  }

  canConfirmImport(): boolean {
    return !!this.importPreview
      && !this.importHasConflicts()
      && this.importPreview.changes.length > 0
      && !this.importSaving;
  }

  cancelImport() {
    this.importPreview = null;
    this.importError = '';
    this.importSaving = false;
  }

  confirmImport() {
    if (!this.canConfirmImport() || !this.importPreview) { return; }

    const previousHidden = this.hiddenEventNames;
    const nextHidden = this.importPreview.nextHiddenEvents;
    this.importSaving = true;
    this.importError = '';
    this.hiddenEventNames = nextHidden;
    this.apiservice.postPerformance(this.buildPerformanceBody({ hiddenEvents: nextHidden })).subscribe({
      next: (saved) => {
        this.importSaving = false;
        this.importPreview = null;
        this.applySavedPerformance(saved);
      },
      error: (err) => {
        this.importSaving = false;
        this.hiddenEventNames = previousHidden;
        this.importError = err?.error?.message ?? 'Failed to apply the imported event listing.';
      }
    });
  }

  downloadMatrix() {
    const events = this.visibleEvents();
    if (!events.length) { return; }

    const stage = this.currentStage.code;
    const includeResult = stage === 'TST';

    const rows: Record<string, string>[] = [];
    for (const ev of events) {
      const visibleRows = this.matrixVisibleRows(ev);
      for (const r of visibleRows) {
        const base: Record<string, string> = {
          'Event': ev.eventName,
          'Event Description': ev.description || '',
          'Type': this.rowTypeLabel(r),
          'DB': r.database,
          'Plan': r.plan,
          'Row Description': r.description,
          'Barcode': r.barcode,
          'Price Code': r.priceCode,
          'Section': r.section,
          'Row': r.row,
          'Seat': r.seat,
          'Ticket Usage': this.usageDisplay(r),
          'Not Scanning': this.notScanning(r) ? 'Yes' : ''
        };
        if (includeResult) {
          base['Row Result'] = this.notScanning(r) ? '' : this.rowResult(r);
        }
        for (const g of this.gateNames) {
          const expected = this.cellExpected(r, g) === 'Y' ? 'accept' : 'reject';
          if (includeResult) {
            const result = this.cellResult(r, g);
            base[g] = `${expected}: ${result}`;
          } else {
            base[g] = expected;
          }
        }
        rows.push(base);
      }
    }

    const filenameStub = stage === 'TC'
      ? 'test_config'
      : stage === 'TM1'
        ? 'tm1_configuration'
        : 'testing_results';
    this.excelService.exportAsExcelFile(rows, this.exportFilename(filenameStub));
  }

  // Exports the confirmed event listing in the venue "Access Control" template
  // layout (one row per event), reproducing the template's colours, fonts,
  // borders, merges, column widths and row heights via ExcelJS. Columns the app
  // has no data for (scan windows, ticket-type message, one-time/multi-day,
  // barcode-file flag) are intentionally left blank to be filled in manually.
  exportAccessControl() {
    const events = this.visibleEvents();
    if (!events.length) { return; }

    // Palette lifted from the venue template.
    const BLUE = 'FF0070C0';       // header / instruction primary fill
    const YELLOW = 'FFFFFF00';     // "additional options" + right-column fills
    const RED = 'FFC00000';        // title text
    const VENUE_FILL = 'FF2F5496'; // VENUE ENTRY header fill (accent1, 25% darker)
    const SEPARATOR = 'FFD9D9D9';  // section-separator grey
    const WHITE = 'FFFFFFFF';
    const thinBox = { top: 'thin', bottom: 'thin', left: 'thin', right: 'thin' };

    const epDateTime = `${this.formatVenueDate(this.performanceTimestamp)} ${this.formatVenueTime(this.performanceTimestamp)}`;

    const COLS = 12;
    const rows: StyledCell[][] = [];
    const pad = (cells: StyledCell[]): StyledCell[] => {
      while (cells.length < COLS) { cells.push({}); }
      return cells;
    };

    // Column layout (A-L). EVENT CODE now holds the record line (e.g.
    // DHADL21P / DHADL21P_2), with dedicated PRICE CODE and BARCODE columns
    // inserted after it.
    //   A VENUE ENTRY | B EVENT CODE | C PRICE CODE | D BARCODE | E EP DATE/TIME
    //   F Event Name | G Start Scan | H Stop Scan | I Event Code/Ticket Type/Section
    //   J Ticket Type & Message | K One-time or Multi-day | L barcode-file flag

    // Row 1-3: title block (merged B1:D3) and "Additional Options" over the
    // yellow columns (title area I1:L2, yellow banner I3:L3).
    rows.push(pad([
      {},
      { value: this.performanceTitle, fill: undefined, fontColor: RED, bold: true, fontSize: 14, fontName: 'Arial', horizontal: 'center', vertical: 'middle' }
    ]));
    rows.push(pad([{}]));
    rows.push(pad([
      {}, {}, {}, {}, {}, {}, {}, {},
      { value: 'Additional Options', fill: YELLOW, bold: true, horizontal: 'center', border: thinBox }
    ]));

    // Row 4: instruction band. Blue region spans B-H, yellow region I-L.
    rows.push(pad([
      {},
      {
        runs: [
          { text: 'Include ' }, { text: 'ALL', bold: true },
          { text: ' barcoded event codes. Indicate if the event code ' },
          { text: 'must not', bold: true, italic: true },
          { text: ' scan in active scan window/scan period.' }
        ],
        fill: BLUE, fontColor: WHITE, horizontal: 'center', wrapText: true, border: thinBox
      },
      { fill: BLUE, border: thinBox },   // C
      { fill: BLUE, border: thinBox },   // D
      { fill: BLUE, border: thinBox },   // E
      { fill: BLUE, border: thinBox },   // F (Event Name)
      {
        runs: [
          { text: 'Active Scan Window/Scan Period', bold: true },
          { text: '\nBy default tickets will scan -12/+12 from EPTIME unless otherwise indicated below.\n\n' },
          { text: 'Include a buffer as changes shouldn\'t be made on the fly.', italic: true }
        ],
        fill: BLUE, fontColor: WHITE, horizontal: 'center', vertical: 'top', wrapText: true, border: thinBox
      },
      { fill: BLUE, border: thinBox },   // H (Stop Scan)
      { value: 'Entrance Specifications', fill: YELLOW, bold: true, vertical: 'top', wrapText: true, border: thinBox },
      {
        runs: [
          { text: 'Ticket Validation \n', bold: true },
          { text: '-Message on JANAM scanner up to 16 characters\n-Turnstiles have limited capabilities\n-Pedestals limited or no validation capabilities' }
        ],
        fill: YELLOW, vertical: 'top', wrapText: true, border: thinBox
      },
      {
        runs: [{ text: 'Ticket Useage\n', bold: true }, { text: 'Default Single Day One-Time Use' }],
        fill: YELLOW, horizontal: 'left', vertical: 'top', wrapText: true, border: thinBox
      },
      {
        runs: [
          { text: 'Third Party Barcode File\n', bold: true },
          { text: 'Barcodes 8-45 alpha-numeric and special charactes * - . $ + % space supported.' }
        ],
        fill: YELLOW, horizontal: 'left', vertical: 'top', wrapText: true, border: thinBox
      }
    ]));

    // Row 5: column headers.
    const hdr = (value: string, fill: string, fontColor?: string, bold = true): StyledCell =>
      ({ value, fill, fontColor, bold, horizontal: 'left', vertical: 'middle', wrapText: true, border: thinBox });
    // Builds a row-5-style section header: the gate label sits in column A
    // (mirroring the template's "VENUE ENTRY (...)" and "INTERNAL - ..." rows),
    // and the standard column headers are re-emitted in B-L.
    const GREY_HEADER = 'FF808080';
    const sectionHeaderRow = (label: string, main: boolean): StyledCell[] => ([
      { value: label, fill: main ? VENUE_FILL : GREY_HEADER, fontColor: WHITE, bold: true, fontSize: 14, horizontal: 'left', vertical: 'middle', wrapText: true, border: thinBox },
      hdr('EVENT CODE', BLUE, WHITE),
      hdr('PRICE CODE', BLUE, WHITE),
      hdr('BARCODE', BLUE, WHITE),
      hdr('EP DATE/TIME', BLUE, WHITE),
      hdr('Event Name/Act Override/PACT Name', BLUE, WHITE),
      hdr('Start Scan', BLUE, WHITE),
      hdr('Stop Scan', BLUE, WHITE),
      hdr('Event Code/Ticket Type/Section', YELLOW, undefined, false),
      hdr('Ticket Type & Message', YELLOW, undefined, false),
      hdr('One-time or Multi-day', YELLOW, undefined, false),
      hdr('Indicate Y if barcode file to be uploaded against this EVENT CODE', YELLOW, undefined, false)
    ]);

    // One line per barcode record. Each barcoded line is grouped by its OWN
    // accept-gate signature, so barcodes within the same event that disagree on
    // a gate land in different sections (mirroring the template's grouped-by-
    // entrance convention). Barcodeless records go under "No Barcode Supplied".
    const NO_BARCODE = '__no_barcode__';
    const groups = new Map<string, { gates: string[]; noBarcode: boolean; lines: { ev: EventViewModel; row: BarcodeRow }[] }>();
    for (const ev of events) {
      const eventRows = [...ev.apiRows, ...ev.extendedRows].filter((r) => !this.isEventSeed(r));
      for (const row of eventRows) {
        const noBarcode = !row.barcode?.trim();
        const gates = noBarcode ? [] : this.rowAcceptGates(row);
        const signature = noBarcode ? NO_BARCODE : gates.join('|');
        const bucket = groups.get(signature);
        if (bucket) { bucket.lines.push({ ev, row }); }
        else { groups.set(signature, { gates, noBarcode, lines: [{ ev, row }] }); }
      }
    }

    // Order sections: all-gates "VENUE ENTRY" first, restricted sets by
    // descending gate count, no-scan set, then "No Barcode Supplied" last.
    const ordered = Array.from(groups.values()).sort((a, b) => {
      if (a.noBarcode !== b.noBarcode) { return a.noBarcode ? 1 : -1; }
      return b.gates.length - a.gates.length;
    });

    const total = this.gateNames.length;
    for (const group of ordered) {
      const isAllGates = !group.noBarcode && total > 0 && group.gates.length === total;
      let label: string;
      if (group.noBarcode) {
        label = 'No Barcode Supplied';
      } else if (isAllGates) {
        label = `VENUE ENTRY (${group.gates.join(', ')})`;
      } else if (group.gates.length === 0) {
        label = 'NOT SCANNING AT ANY GATE';
      } else {
        // Prefer the shorter description: "does not scan at" the excluded gates,
        // or "scans at" the accepted gates — whichever names fewer gates.
        const excluded = this.gateNames.filter((g) => !group.gates.includes(g));
        label = excluded.length <= group.gates.length
          ? `VENUE ENTRY — DOES NOT SCAN AT: ${excluded.join(', ')}`
          : `SCANS AT: ${group.gates.join(', ')} ONLY`;
      }
      rows.push(sectionHeaderRow(label, isAllGates));

      // Within a section, keep a light DB separator when the database changes.
      const sorted = [...group.lines].sort((a, b) =>
        (a.ev.database || '').localeCompare(b.ev.database || '')
        || a.ev.eventName.localeCompare(b.ev.eventName)
        || (a.row.plan || '').localeCompare(b.row.plan || ''));
      let lastDb: string | null = null;

      for (const { ev, row } of sorted) {
        const db = (ev.database || '').trim() || 'Other';
        if (db !== lastDb) {
          const sepCell = (value?: string, bold = false): StyledCell =>
            ({ value, fill: SEPARATOR, bold, fontSize: 10, border: { left: 'thin', right: 'thin' } });
          rows.push(pad([sepCell(), sepCell(db, true)]));
          lastDb = db;
        }

        const cell = (value: string, opts: Partial<StyledCell> = {}): StyledCell =>
          ({ value, fontSize: 10, border: thinBox, ...opts });
        rows.push([
          cell(''),                                        // A VENUE ENTRY
          cell((row.description || '').trim() || ev.eventName), // B EVENT CODE = record line (e.g. DHADL21P, DHADL21P_2)
          cell((row.priceCode || '').trim()),              // C PRICE CODE
          cell((row.barcode || '').trim()),                // D BARCODE
          cell(epDateTime, { horizontal: 'right' }),       // E EP DATE/TIME
          cell(ev.description || ''),                      // F Event Name / Act Override
          cell('', { horizontal: 'center' }),              // G Start Scan
          cell('', { horizontal: 'center' }),              // H Stop Scan
          cell(this.lineTicketDetail(row)),                // I Event Code/Ticket Type/Section
          cell(''),                                        // J Ticket Type & Message
          cell(this.usageDisplay(row)),                    // K One-time or Multi-day
          cell('')                                         // L barcode-file flag
        ]);
      }
    }

    this.excelService.exportStyledSheet(rows, this.exportFilename('access_control'), {
      merges: ['B1:D3', 'I1:L2', 'I3:L3', 'B4:E4', 'G4:H4'],
      colWidths: [63.14, 27.43, 12, 16, 14.86, 63.57, 10, 10.14, 15.71, 21.29, 11.29, 30.43],
      rowHeights: [12, 12.75, undefined, 104.25, 60]
    });
  }

  // Returns the gates a single barcode row scans at (accepts), in gateNames
  // order. Entitlement-only rows scan nowhere.
  private rowAcceptGates(row: BarcodeRow): string[] {
    if (this.notScanning(row)) { return []; }
    return this.gateNames.filter((gate) => this.cellExpected(row, gate) === 'Y');
  }

  // Composes the "Event Code/Ticket Type/Section" cell for a barcode line.
  // Price code and barcode now have their own dedicated columns, so this
  // carries only the section/row/seat locating detail.
  private lineTicketDetail(row: BarcodeRow): string {
    return [row.section, row.row, row.seat]
      .map((v) => (v || '').trim())
      .filter(Boolean)
      .join('/');
  }

  private exportFilename(stub: string): string {
    const safeVenue = (this.venueName || this.venueID).replace(/[^a-z0-9]+/gi, '_');
    return `${stub}_${safeVenue}_${this.performanceDate}`;
  }

  toggleEvent(ev: EventViewModel) {
    ev.expanded = !ev.expanded;
  }

  // Builds a full performance save body from current component state. Every
  // postPerformance call routes through here so no persisted field (hidden
  // events, custom events, per-event scan metadata) is dropped on save — the
  // backend replaces the record rather than merging. Pass overrides for the
  // fields a given save is actually changing.
  private buildPerformanceBody(overrides: Partial<PerformanceCreateRequest> = {}): PerformanceCreateRequest {
    return {
      venueID: this.venueID,
      timestamp: this.performanceTimestamp,
      description: this.performanceDescription,
      status: this.performanceStatus,
      hiddenEvents: this.hiddenEventNames,
      customArchticsEvents: this.customArchticsEventNames,
      customHostEvents: this.customHostEventNames,
      eventScanMeta: this.eventScanMeta,
      ...overrides
    };
  }

  toggleKeep(ev: EventViewModel) {
    if (this.keepSaving) { return; }
    const previous = ev.keep;
    ev.keep = !ev.keep;

    const next = ev.keep
      ? this.hiddenEventNames.filter((n) => n !== ev.eventName)
      : Array.from(new Set([...this.hiddenEventNames, ev.eventName]));

    const previousHidden = this.hiddenEventNames;
    this.hiddenEventNames = next;
    this.keepSaving = true;
    this.apiservice.postPerformance(this.buildPerformanceBody({ hiddenEvents: next })).subscribe({
      next: (saved) => {
        this.keepSaving = false;
        this.applySavedPerformance(saved);
      },
      error: (err) => {
        this.keepSaving = false;
        ev.keep = previous;
        this.hiddenEventNames = previousHidden;
        this.loadError = err?.error?.message ?? 'Failed to save event keep/remove status.';
      }
    });
  }

  // Coerces a raw eventScanMeta map from the API into a clean map: trims and
  // caps messages, validates usage against the known values, and drops empty
  // entries so an event with no metadata isn't persisted.
  private sanitiseEventScanMeta(raw: unknown): Record<string, EventScanMeta> {
    const out: Record<string, EventScanMeta> = {};
    if (!raw || typeof raw !== 'object') { return out; }
    const usages: TicketUsage[] = ['one_time', 'multi_day', 'either'];
    for (const [eventName, value] of Object.entries(raw as Record<string, unknown>)) {
      if (!value || typeof value !== 'object') { continue; }
      const entry = value as { message?: unknown; usage?: unknown };
      const message = typeof entry.message === 'string'
        ? entry.message.trim().slice(0, SCANNER_MESSAGE_MAX_LENGTH)
        : '';
      const usage = usages.includes(entry.usage as TicketUsage) ? (entry.usage as TicketUsage) : undefined;
      if (message || usage) {
        out[eventName] = { ...(message ? { message } : {}), ...(usage ? { usage } : {}) };
      }
    }
    return out;
  }

  // The saved metadata for an event code, or an empty object. Used by template
  // bindings and edit handlers.
  eventMeta(eventName: string): EventScanMeta {
    return this.eventScanMeta[eventName] ?? {};
  }

  // Handles an edit to an event's scanner message. Caps at the scanner limit,
  // updates local state, then persists. Empty clears the message.
  setEventMessage(eventName: string, rawMessage: string): void {
    const message = (rawMessage || '').slice(0, SCANNER_MESSAGE_MAX_LENGTH);
    const current = this.eventMeta(eventName);
    if ((current.message ?? '') === message) { return; }
    this.commitEventMeta(eventName, { ...current, message });
  }

  // Handles an edit to an event's one-time/multi-day usage. Empty clears it.
  setEventUsage(eventName: string, usage: TicketUsage | ''): void {
    const current = this.eventMeta(eventName);
    const next: EventScanMeta = { ...current };
    if (usage) { next.usage = usage; } else { delete next.usage; }
    this.commitEventMeta(eventName, next);
  }

  // Writes an event's metadata into the map (dropping empty entries) and saves
  // the performance record. Rolls back local state on error.
  private commitEventMeta(eventName: string, next: EventScanMeta): void {
    if (this.metaSaving) { return; }
    const previous = this.eventScanMeta;
    const updated = { ...this.eventScanMeta };
    const message = (next.message ?? '').trim();
    if (message || next.usage) {
      updated[eventName] = { ...(message ? { message } : {}), ...(next.usage ? { usage: next.usage } : {}) };
    } else {
      delete updated[eventName];
    }

    this.eventScanMeta = updated;
    this.metaSaving = true;
    this.apiservice.postPerformance(this.buildPerformanceBody({ eventScanMeta: updated })).subscribe({
      next: (saved) => {
        this.metaSaving = false;
        this.applySavedPerformance(saved);
      },
      error: (err) => {
        this.metaSaving = false;
        this.eventScanMeta = previous;
        this.loadError = err?.error?.message ?? 'Failed to save ticket type / message.';
      }
    });
  }

  startDraft(ev: EventViewModel) {
    if (this.hasOpenDraft()) { return; }
    ev.expanded = true;
    ev.draft = {
      database: ev.database,
      plan: '',
      description: '',
      barcode: '',
      priceCode: '',
      section: '',
      row: '',
      seat: ''
    };
    ev.draftErrors = { db: false, locator: false, barcode: false };
  }

  cancelDraft(ev: EventViewModel) {
    ev.draft = null;
    ev.draftErrors = { db: false, locator: false, barcode: false };
  }

  validateDraft(ev: EventViewModel): { ok: boolean } {
    if (!ev.draft) { return { ok: false }; }
    const dbOk = ev.draft.database.trim().length > 0;
    const locatorOk = [ev.draft.priceCode, ev.draft.section, ev.draft.row, ev.draft.seat]
      .some((v) => v.trim().length > 0);
    const barcodeOk = ev.draft.barcode.trim().length > 0;
    ev.draftErrors = { db: !dbOk, locator: !locatorOk, barcode: !barcodeOk };
    return { ok: dbOk && locatorOk && barcodeOk };
  }

  saveDraft(ev: EventViewModel) {
    if (!ev.draft) { return; }
    const validation = this.validateDraft(ev);
    if (!validation.ok) { return; }

    const nextItem = this.nextItemNumber();
    const barcodeText = ev.draft.barcode.trim();
    const draft = ev.draft;
    const record: ScanTestBarcode = {
      key: this.scanTestKey,
      item: nextItem,
      barcode: barcodeText,
      gateArray: buildDefaultGateArray(this.gateNames),
      database_name: draft.database.trim(),
      event_name: ev.eventName,
      plan_event_name: draft.plan.trim(),
      fantm_event_name: draft.description.trim(),
      price_code: draft.priceCode.trim(),
      section: draft.section.trim(),
      row: draft.row.trim(),
      seat: draft.seat.trim(),
      extended: true,
      event_type: ev.eventType
    };

    this.saving = true;
    this.apiservice.postScanTest(record).subscribe({
      next: () => {
        ev.extendedRows.push({
          item: nextItem,
          database: draft.database.trim(),
          plan: draft.plan.trim(),
          description: draft.description.trim(),
          barcode: barcodeText,
          priceCode: draft.priceCode.trim(),
          section: draft.section.trim(),
          row: draft.row.trim(),
          seat: draft.seat.trim(),
          usageLabel: record.host_ticket_type?.trim() || '',
          numSeats: '',
          extended: true,
          eventType: ev.eventType,
          raw: record,
          editingBarcode: false,
          barcodeDraft: '',
          barcodeError: false
        });
        ev.draft = null;
        ev.draftErrors = { db: false, locator: false, barcode: false };
        this.refreshDatabaseFilter();
        this.saving = false;
      },
      error: (err) => {
        this.saving = false;
        this.loadError = err?.error?.message ?? 'Failed to save extended test row.';
      }
    });
  }

  private refreshDatabaseFilter() {
    const seen = new Set<string>();
    for (const ev of this.events) {
      for (const r of [...ev.apiRows, ...ev.extendedRows]) {
        const db = (r.database || '').trim();
        if (db) { seen.add(db); }
      }
    }
    const previousAll = new Set(this.allDatabases);
    this.allDatabases = Array.from(seen).sort((a, b) => a.localeCompare(b));
    const next = new Set<string>();
    for (const db of this.allDatabases) {
      const isNew = !previousAll.has(db);
      if (!this.selectedDatabases.size || this.selectedDatabases.has(db) || isNew) {
        next.add(db);
      }
    }
    if (!next.size && this.allDatabases.length) {
      for (const db of this.allDatabases) { next.add(db); }
    }
    this.selectedDatabases = next;
  }

  hasOpenDraft(): boolean {
    return this.eventDraft !== null || this.events.some((ev) => ev.draft !== null);
  }

  /** The event whose matrix draft (host barcode / extended test) is currently open, if any. */
  get draftEvent(): EventViewModel | null {
    return this.events.find((ev) => ev.draft !== null) ?? null;
  }

  startEventDraft() {
    if (this.hasOpenDraft() || this.saving) { return; }
    this.eventDraft = {
      eventName: '',
      description: '',
      database: '',
      eventType: 'archtics',
      errors: { eventName: false, database: false, duplicate: false }
    };
  }

  cancelEventDraft() {
    this.eventDraft = null;
  }

  setEventDraftType(type: EventType) {
    if (!this.eventDraft) { return; }
    this.eventDraft.eventType = type;
    if (type === 'host') {
      this.eventDraft.errors.database = false;
    }
  }

  validateEventDraft(): boolean {
    if (!this.eventDraft) { return false; }
    const name = this.eventDraft.eventName.trim();
    const isHost = this.eventDraft.eventType === 'host';
    const db = isHost ? 'HOST' : this.eventDraft.database.trim();
    const duplicate = !!name && this.events.some((ev) => ev.eventName.toLowerCase() === name.toLowerCase());
    this.eventDraft.errors = {
      eventName: !name,
      database: !isHost && !db,
      duplicate
    };
    return !!name && !!db && !duplicate;
  }

  saveEventDraft() {
    if (!this.eventDraft) { return; }
    if (!this.validateEventDraft()) { return; }

    const name = this.eventDraft.eventName.trim();
    const description = this.eventDraft.description.trim();
    const eventType: EventType = this.eventDraft.eventType;
    const database = eventType === 'host' ? 'HOST' : this.eventDraft.database.trim();
    const item = this.nextItemNumber();

    const seed: ScanTestBarcode = {
      key: this.scanTestKey,
      item,
      barcode: '',
      gateArray: buildDefaultGateArray(this.gateNames),
      database_name: database,
      event_name: name,
      plan_event_name: '',
      fantm_event_name: description,
      price_code: '',
      section: '',
      row: '',
      seat: '',
      extended: true,
      event_type: eventType
    };

    this.saving = true;
    this.apiservice.postScanTest(seed).subscribe({
      next: () => {
        const seedRow: BarcodeRow = {
          item,
          database,
          plan: '',
          description,
          barcode: '',
          priceCode: '',
          section: '',
          row: '',
          seat: '',
          usageLabel: '',
          numSeats: '',
          extended: true,
          eventType,
          raw: seed,
          editingBarcode: false,
          barcodeDraft: '',
          barcodeError: false
        };
        const newEvent: EventViewModel = {
          eventName: name,
          database,
          description,
          eventType,
          apiRows: [],
          extendedRows: [seedRow],
          expanded: true,
          matrixExpanded: true,
          keep: true,
          draft: null,
          draftErrors: { db: false, locator: false, barcode: false }
        };
        this.events = [...this.events, newEvent].sort((a, b) => a.eventName.localeCompare(b.eventName));
        this.refreshDatabaseFilter();
        this.eventDraft = null;
        this.recordCustomEvent(name, eventType);
      },
      error: (err) => {
        this.saving = false;
        this.loadError = err?.error?.message ?? 'Failed to save new event.';
      }
    });
  }

  private recordCustomEvent(name: string, eventType: EventType) {
    const isHost = eventType === 'host';
    const list = isHost ? this.customHostEventNames : this.customArchticsEventNames;
    if (list.includes(name)) {
      this.saving = false;
      return;
    }
    const nextHost = isHost ? [...this.customHostEventNames, name] : this.customHostEventNames;
    const nextArchtics = isHost ? this.customArchticsEventNames : [...this.customArchticsEventNames, name];

    this.apiservice.postPerformance(this.buildPerformanceBody({
      customArchticsEvents: nextArchtics,
      customHostEvents: nextHost
    })).subscribe({
      next: (saved) => {
        this.applySavedPerformance(saved);
        this.saving = false;
      },
      error: (err) => {
        this.saving = false;
        this.loadError = err?.error?.message ?? 'Event saved, but failed to record audit entry.';
      }
    });
  }

  startBarcodeEdit(row: BarcodeRow) {
    row.editingBarcode = true;
    row.barcodeDraft = row.barcode || '';
    row.barcodeError = false;
  }

  cancelBarcodeEdit(row: BarcodeRow) {
    row.editingBarcode = false;
    row.barcodeDraft = '';
    row.barcodeError = false;
  }

  saveBarcode(_ev: EventViewModel, row: BarcodeRow) {
    const value = (row.barcodeDraft || '').trim();
    if (!value) {
      row.barcodeError = true;
      return;
    }
    if (value === row.barcode) {
      row.editingBarcode = false;
      row.barcodeDraft = '';
      return;
    }
    row.barcodeError = false;

    const updated: ScanTestBarcode = {
      ...row.raw,
      item: row.item,
      barcode: value,
      gateArray: row.raw.gateArray ?? buildDefaultGateArray(this.gateNames)
    };

    this.saving = true;
    this.apiservice.postScanTest(updated).subscribe({
      next: () => {
        row.barcode = value;
        row.raw = updated;
        row.editingBarcode = false;
        row.barcodeDraft = '';
        this.saving = false;
      },
      error: (err) => {
        this.saving = false;
        this.loadError = err?.error?.message ?? 'Failed to save barcode.';
      }
    });
  }

  removeExtended(ev: EventViewModel, row: BarcodeRow) {
    if (!row.barcode) {
      this.loadError = 'Cannot delete an extended test that has no barcode — please contact support.';
      return;
    }
    const confirmed = window.confirm(`Remove extended test for barcode ${row.barcode}?`);
    if (!confirmed) { return; }

    this.saving = true;
    this.apiservice.deleteScanTests(this.scanTestKey, row.barcode).subscribe({
      next: () => {
        ev.extendedRows = ev.extendedRows.filter((r) => r.item !== row.item);
        this.saving = false;
      },
      error: (err) => {
        this.saving = false;
        this.loadError = err?.error?.message ?? 'Failed to remove extended test.';
      }
    });
  }

  formatAuditTime(epoch: number): string {
    return new Intl.DateTimeFormat('en-AU', {
      timeZone: this.venueTimezone || 'UTC',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(new Date(epoch * 1000));
  }

  totalBarcodes(ev: EventViewModel): number {
    const all = [...ev.apiRows, ...ev.extendedRows].filter((r) => !this.isEventSeed(r));
    return all.length;
  }

  private isTestableRow(row: BarcodeRow): boolean {
    if (this.isEventSeed(row)) { return false; }
    if (this.notScanning(row)) { return false; }
    if (!row.barcode) { return false; }
    return true;
  }

  totalApi(): number {
    return this.events.reduce((acc, ev) =>
      ev.keep ? acc + ev.apiRows.filter((r) => this.isTestableRow(r) && r.eventType === 'archtics').length : acc, 0);
  }

  totalExtended(): number {
    return this.events.reduce((acc, ev) =>
      ev.keep ? acc + ev.extendedRows.filter((r) => this.isTestableRow(r) && r.eventType === 'archtics').length : acc, 0);
  }

  totalHost(): number {
    return this.events.reduce((acc, ev) => {
      if (!ev.keep) { return acc; }
      const hostRows = [...ev.apiRows, ...ev.extendedRows]
        .filter((r) => this.isTestableRow(r) && r.eventType === 'host');
      return acc + hostRows.length;
    }, 0);
  }

  visibleHostRows(ev: EventViewModel): BarcodeRow[] {
    return [...ev.apiRows, ...ev.extendedRows]
      .filter((r) => !this.isEventSeed(r))
      .filter((r) => r.eventType === 'host')
      .filter((r) => this.rowMatchesDbFilter(r));
  }

  rowTypeLabel(row: BarcodeRow): string {
    if (row.eventType === 'host') { return 'Host'; }
    return row.extended ? 'Extended' : 'Archtics';
  }

  totalRowsAll(): number {
    return this.totalApi() + this.totalExtended();
  }

  keptEventsCount(): number {
    return this.events.reduce((acc, ev) => ev.keep ? acc + 1 : acc, 0);
  }

  gatesWithRejects(): number {
    const gatesUsed = new Set<string>();
    for (const ev of this.events) {
      if (!ev.keep) { continue; }
      for (const r of [...ev.apiRows, ...ev.extendedRows]) {
        if (this.isEventSeed(r)) { continue; }
        for (const g of this.gateNames) {
          if (this.cellExpected(r, g) === 'N') { gatesUsed.add(g); }
        }
      }
    }
    return gatesUsed.size;
  }

  get matrixIsReadOnly(): boolean {
    if (this.currentStage.code === 'TC') { return false; }
    if (this.currentStage.code === 'TM1' && this.tm1EditUnlocked) { return false; }
    return true;
  }

  get canEditTm1(): boolean {
    return this.currentStage.code === 'TM1' && (this.isTicketmaster || this.isGlobalAdmin);
  }

  get tm1EditPhrase(): string {
    return PerformanceDetailComponent.TM1_EDIT_PHRASE;
  }

  tm1EditPhraseOk(): boolean {
    return this.tm1EditConfirmation.trim().toLowerCase() === PerformanceDetailComponent.TM1_EDIT_PHRASE.toLowerCase();
  }

  openTm1EditModal() {
    if (!this.canEditTm1) { return; }
    this.tm1EditConfirmation = '';
    this.showTm1EditModal = true;
  }

  cancelTm1Edit() {
    this.showTm1EditModal = false;
    this.tm1EditConfirmation = '';
  }

  confirmTm1Edit() {
    if (!this.tm1EditPhraseOk()) { return; }
    this.tm1EditUnlocked = true;
    this.showTm1EditModal = false;
    this.tm1EditConfirmation = '';
  }

  saveTm1Changes() {
    this.tm1EditUnlocked = false;
  }

  get showsMatrix(): boolean {
    return ['TC', 'TM1', 'TST'].includes(this.currentStage.code);
  }

  get clientConfirmation(): { user: string; time: number } | null {
    const entry = this.statusHistory.find((h) => h.status === 'client_confirmed');
    if (!entry) { return null; }
    return { user: entry.username, time: entry.time };
  }

  get tm1Configuration(): { user: string; time: number } | null {
    const entry = this.statusHistory.find((h) => h.status === 'tm1_entry_configured');
    if (!entry) { return null; }
    return { user: entry.username, time: entry.time };
  }

  toggleMatrixExpanded(ev: EventViewModel) {
    ev.matrixExpanded = !ev.matrixExpanded;
  }

  isEventSeed(row: BarcodeRow): boolean {
    // Rule-seeded rows carry a ruleSeedKey and are always shown so the tester can
    // fill in a barcode, even a Ticket Usage row that has no section/row/price.
    if (row.raw.ruleSeedKey) { return false; }
    return row.extended
      && !row.barcode
      && !row.priceCode
      && !row.section
      && !row.row
      && !row.seat
      && !row.plan;
  }

  matrixVisibleRows(ev: EventViewModel): BarcodeRow[] {
    const rows = this.matrixOnlyExtended
      ? [...ev.extendedRows]
      : [...ev.apiRows, ...ev.extendedRows];
    return rows
      .filter((r) => !this.isEventSeed(r))
      .filter((r) => this.currentStage.code !== 'TST' || !!r.barcode)
      .filter((r) => this.rowMatchesDbFilter(r))
      .filter((r) => !this.matrixOnlyFailed
        || this.currentStage.code !== 'TST'
        || (!this.notScanning(r) && this.rowResult(r) === 'fail'));
  }

  visibleEvents(): EventViewModel[] {
    return this.events.filter((ev) => {
      if (this.currentStage.code !== 'EL' && !ev.keep) { return false; }
      const realRows = [...ev.apiRows, ...ev.extendedRows].filter((r) => !this.isEventSeed(r));
      if (realRows.length === 0) {
        if (this.matrixOnlyExtended) { return false; }
        const db = (ev.database || '').trim();
        if (!db || !this.allDatabases.length) { return true; }
        return this.selectedDatabases.has(db);
      }
      return this.matrixVisibleRows(ev).length > 0;
    });
  }

  visibleApiRows(ev: EventViewModel): BarcodeRow[] {
    return ev.apiRows
      .filter((r) => !this.isEventSeed(r))
      .filter((r) => this.rowMatchesDbFilter(r));
  }

  visibleExtendedRows(ev: EventViewModel): BarcodeRow[] {
    return ev.extendedRows
      .filter((r) => !this.isEventSeed(r))
      .filter((r) => this.rowMatchesDbFilter(r));
  }

  visibleEventsForListing(): EventViewModel[] {
    return this.events.filter((ev) => {
      const realRows = [...ev.apiRows, ...ev.extendedRows].filter((r) => !this.isEventSeed(r));
      if (realRows.length === 0) {
        const db = (ev.database || '').trim();
        if (!db || !this.allDatabases.length) { return true; }
        return this.selectedDatabases.has(db);
      }
      return this.visibleApiRows(ev).length + this.visibleExtendedRows(ev).length > 0;
    });
  }

  isDatabaseSelected(db: string): boolean {
    return this.selectedDatabases.has(db);
  }

  toggleDatabase(db: string) {
    if (this.selectedDatabases.has(db)) {
      this.selectedDatabases.delete(db);
    } else {
      this.selectedDatabases.add(db);
    }
  }

  selectAllDatabases() {
    this.selectedDatabases = new Set(this.allDatabases);
  }

  clearDatabases() {
    this.selectedDatabases = new Set();
  }

  hasDbFilterActive(): boolean {
    return this.allDatabases.length > 1
      && this.selectedDatabases.size !== this.allDatabases.length;
  }

  allDatabasesSelected(): boolean {
    return this.allDatabases.length > 0
      && this.selectedDatabases.size === this.allDatabases.length;
  }

  private rowMatchesDbFilter(row: BarcodeRow): boolean {
    if (!this.allDatabases.length) { return true; }
    const db = (row.database || '').trim();
    if (!db) { return true; }
    return this.selectedDatabases.has(db);
  }

  notScanning(row: BarcodeRow): boolean {
    const plan = (row.raw.plan ?? '').toString().trim().toUpperCase();
    const entitlement = (row.raw.entitlement ?? '').toString().trim().toUpperCase();
    return entitlement === 'Y' && plan !== 'Y';
  }

  cellExpected(row: BarcodeRow, gate: string): 'Y' | 'N' {
    const entry = row.raw.gateArray?.[gate];
    return entry?.expected === 'N' ? 'N' : 'Y';
  }

  cellResult(row: BarcodeRow, gate: string): 'pass' | 'fail' | 'pending' {
    if (this.rowHasActiveRun(row)) { return 'pending'; }
    const entry = row.raw.gateArray?.[gate];
    const last = entry?.lastResult;
    if (!last) { return 'pending'; }
    const expected = entry?.expected === 'N' ? 'N' : 'Y';
    return last === expected ? 'pass' : 'fail';
  }

  rowResult(row: BarcodeRow): 'pass' | 'fail' | 'pending' {
    if (this.notScanning(row)) { return 'pending'; }
    if (!row.barcode) { return 'pending'; }
    if (this.rowHasActiveRun(row)) { return 'pending'; }
    let anyTested = false;
    for (const g of this.gateNames) {
      const r = this.cellResult(row, g);
      if (r === 'fail') { return 'fail'; }
      if (r === 'pass') { anyTested = true; }
    }
    return anyTested ? 'pass' : 'pending';
  }

  private rowHasActiveRun(row: BarcodeRow): boolean {
    const run = this.currentRun;
    if (!run) { return false; }
    if (run.status !== 'queued' && run.status !== 'running') { return false; }
    if (run.item == null) { return true; }
    return Number(run.item) === Number(row.item);
  }

  scanCounts(): { pass: number; fail: number; pending: number; total: number } {
    const c = this.countsTotal();
    return { ...c, total: c.pass + c.fail + c.pending };
  }

  scanProgressPercent(): number {
    const c = this.scanCounts();
    if (!c.total) { return 0; }
    const completed = c.pass + c.fail;
    return Math.round((completed / c.total) * 100);
  }

  totalEventsKept(): number {
    return this.keptEventsCount();
  }

  lastRunFormatted(): { date: string; time: string } | null {
    const last = this.lastRunTime();
    if (!last) { return null; }
    const tz = this.venueTimezone || 'UTC';
    const date = new Intl.DateTimeFormat('en-AU', {
      timeZone: tz,
      day: '2-digit',
      month: 'short'
    }).format(new Date(last * 1000));
    const time = new Intl.DateTimeFormat('en-AU', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(new Date(last * 1000));
    return { date, time };
  }

  countsTotal(): { pass: number; fail: number; pending: number } {
    const out = { pass: 0, fail: 0, pending: 0 };
    for (const ev of this.events) {
      if (!ev.keep) { continue; }
      for (const row of [...ev.apiRows, ...ev.extendedRows]) {
        if (!this.isTestableRow(row)) { continue; }
        out[this.rowResult(row)]++;
      }
    }
    return out;
  }

  countsForEvent(ev: EventViewModel): { pass: number; fail: number; pending: number } {
    const out = { pass: 0, fail: 0, pending: 0 };
    for (const row of [...ev.apiRows, ...ev.extendedRows]) {
      if (!this.isTestableRow(row)) { continue; }
      out[this.rowResult(row)]++;
    }
    return out;
  }

  lastRunTime(): number | null {
    let max = 0;
    for (const ev of this.events) {
      for (const row of [...ev.apiRows, ...ev.extendedRows]) {
        const ga = row.raw.gateArray || {};
        for (const g of Object.keys(ga)) {
          const history = ga[g]?.resultHistory ?? [];
          for (const h of history) {
            const t = Number(h.testTime);
            if (t > max) { max = t; }
          }
        }
      }
    }
    return max > 0 ? max : null;
  }

  hasFailedRows(): boolean {
    return this.countsTotal().fail > 0;
  }

  runAllTests() {
    if (!this.canRunTests()) { return; }
    this.runQueue = [];
    this.runQueueTotal = 0;
    this.clearRowResults();
    this.dispatchTestRun(undefined);
  }

  runRowTest(row: BarcodeRow) {
    if (!this.canRunTests() || !row.barcode || this.notScanning(row)) { return; }
    this.runQueue = [];
    this.runQueueTotal = 0;
    this.clearRowResults(row);
    this.testRunningRow = row.item;
    this.dispatchTestRun(row.item, () => { this.testRunningRow = null; });
  }

  runSelectedTests() {
    if (!this.canRunTests()) { return; }
    const rows = this.selectedRowsResolved().filter((r) => !!r.barcode && !this.notScanning(r));
    if (!rows.length) { return; }
    for (const row of rows) { this.clearRowResults(row); }
    this.runQueue = rows.map((r) => r.item);
    this.runQueueTotal = this.runQueue.length;
    this.runNextQueued();
  }

  private runNextQueued() {
    const nextItem = this.runQueue.shift();
    if (nextItem == null) {
      this.testRunningRow = null;
      this.runQueueTotal = 0;
      return;
    }
    this.testRunningRow = nextItem;
    this.dispatchTestRun(nextItem, () => {
      if (this.runQueue.length) {
        this.runNextQueued();
      } else {
        this.testRunningRow = null;
        this.runQueueTotal = 0;
      }
    });
  }

  get runSelectedLabel(): string {
    if (this.runQueueTotal > 0) {
      const current = this.runQueueTotal - this.runQueue.length;
      return `Running ${current} of ${this.runQueueTotal}…`;
    }
    return `Run ${this.runnableSelectedCount} selected`;
  }

  get runnableSelectedCount(): number {
    return this.selectedRowsResolved()
      .filter((r) => !!r.barcode && !this.notScanning(r))
      .length;
  }

  private clearRowResults(target?: BarcodeRow) {
    const clearGateArray = (raw: ScanTestBarcode): ScanTestBarcode => {
      const gateArray: typeof raw.gateArray = {};
      for (const g of Object.keys(raw.gateArray || {})) {
        const entry = raw.gateArray[g];
        gateArray[g] = { ...entry, lastResult: '' };
      }
      return { ...raw, gateArray };
    };

    if (target) {
      target.raw = clearGateArray(target.raw);
      return;
    }

    for (const ev of this.events) {
      if (!ev.keep) { continue; }
      for (const row of [...ev.apiRows, ...ev.extendedRows]) {
        if (!this.isTestableRow(row)) { continue; }
        row.raw = clearGateArray(row.raw);
      }
    }
  }

  canRunTests(): boolean {
    return this.currentStage.code === 'TST' && !this.testRunning && !!this.scanTestKey;
  }

  get isEventDayReady(): boolean {
    return this.performanceStatus === 'event_day_ready';
  }

  get allTestsPassed(): boolean {
    const c = this.countsTotal();
    if (c.pass + c.fail + c.pending === 0) { return false; }
    return c.fail === 0 && c.pending === 0;
  }

  get canMarkReady(): boolean {
    if (this.currentStage.code !== 'TST') { return false; }
    if (this.isEventDayReady) { return false; }
    if (this.testRunning || this.markReadySaving) { return false; }
    return this.allTestsPassed;
  }

  get markReadyTooltip(): string {
    if (this.isEventDayReady) { return 'Performance is already marked ready'; }
    if (this.allTestsPassed) { return 'All tests passed — close out the performance'; }
    return 'Available once every barcode passes at every gate';
  }

  markReady() {
    if (!this.canMarkReady) { return; }
    this.markReadySaving = true;
    this.markReadyError = '';
    this.apiservice.postPerformance(this.buildPerformanceBody({ status: 'event_day_ready' })).subscribe({
      next: (saved) => {
        this.applySavedPerformance(saved);
        this.markReadySaving = false;
      },
      error: (err) => {
        this.markReadySaving = false;
        this.markReadyError = err?.error?.message ?? 'Failed to mark performance ready.';
      }
    });
  }

  get runProgressPercent(): number {
    return Number(this.currentRun?.progressPercent ?? 0) || 0;
  }

  get runCompletedTests(): number {
    return Number(this.currentRun?.completedTests ?? 0) || 0;
  }

  get runTotalTests(): number {
    return Number(this.currentRun?.totalTests ?? 0) || 0;
  }

  get runStatusLabel(): string {
    if (!this.currentRun) { return ''; }
    switch (this.currentRun.status) {
      case 'queued': return 'Queued';
      case 'running': return `Running ${this.runProgressPercent}%`;
      case 'complete': return 'Complete';
      case 'failed': return 'Failed';
      default: return this.currentRun.status;
    }
  }

  get hasActiveOrFinishedRun(): boolean {
    return !!this.currentRun;
  }

  private dispatchTestRun(item: number | undefined, onDone?: () => void) {
    this.testRunning = true;
    this.testRunError = '';
    this.currentRun = null;

    this.apiservice.startScanTestRun(this.scanTestKey, item).subscribe({
      next: (started) => {
        this.currentRun = {
          runId: started.runId,
          status: started.status,
          startedAt: started.startedAt,
          item: started.item
        };
        this.beginPolling(started.runId, onDone);
      },
      error: (err) => {
        this.testRunning = false;
        this.testRunningRow = null;
        const msg = err?.error?.message ?? 'Failed to start scan test.';
        const inProgressId = err?.error?.runId;
        if (err?.status === 409 && inProgressId) {
          this.testRunError = `${msg} Polling existing run.`;
          this.testRunning = true;
          this.beginPolling(inProgressId, onDone);
        } else {
          this.testRunError = msg;
          if (onDone) { onDone(); }
        }
      }
    });
  }

  private beginPolling(runId: string, onDone?: () => void) {
    this.stopPolling();
    const poll = () => {
      this.apiservice.getScanTestRun(this.scanTestKey, runId).subscribe({
        next: (status) => {
          this.currentRun = status;
          if (status.status === 'complete') {
            this.stopPolling();
            this.refreshAfterRun(onDone);
          } else if (status.status === 'failed') {
            this.stopPolling();
            this.testRunning = false;
            this.testRunningRow = null;
            this.testRunError = status.error || 'Scan test run failed.';
            if (onDone) { onDone(); }
          } else {
            this.pollHandle = setTimeout(poll, PerformanceDetailComponent.POLL_INTERVAL_MS);
          }
        },
        error: (err) => {
          this.stopPolling();
          this.testRunning = false;
          this.testRunningRow = null;
          this.testRunError = err?.error?.message ?? 'Failed to read scan test run status.';
          if (onDone) { onDone(); }
        }
      });
    };
    this.pollHandle = setTimeout(poll, PerformanceDetailComponent.POLL_INTERVAL_MS);
  }

  private stopPolling() {
    if (this.pollHandle != null) {
      clearTimeout(this.pollHandle);
      this.pollHandle = null;
    }
  }

  private refreshAfterRun(onDone?: () => void) {
    this.apiservice.getScanTests(this.venueID, this.performanceTimestamp).subscribe({
      next: (records) => {
        this.applyTestRecords((records as ScanTestRecord[]) ?? []);
        this.testRunning = false;
        this.testRunningRow = null;
        if (onDone) { onDone(); }
      },
      error: (err) => {
        this.testRunning = false;
        this.testRunningRow = null;
        this.testRunError = err?.error?.message ?? 'Run completed but failed to load results.';
        if (onDone) { onDone(); }
      }
    });
  }

  private applyTestRecords(records: ScanTestRecord[]) {
    const byItem = new Map<number, ScanTestBarcode>();
    for (const r of records) {
      if (isMasterConfig(r)) { continue; }
      const normalised = { ...r, item: Number(r.item) } as ScanTestBarcode;
      byItem.set(normalised.item, normalised);
    }
    for (const ev of this.events) {
      for (const row of [...ev.apiRows, ...ev.extendedRows]) {
        const updated = byItem.get(row.item);
        if (updated) { row.raw = updated; }
      }
    }
  }

  findRowByItem(item: number): BarcodeRow | null {
    for (const ev of this.events) {
      for (const r of [...ev.apiRows, ...ev.extendedRows]) {
        if (Number(r.item) === Number(item)) { return r; }
      }
    }
    return null;
  }

  eventForRow(row: BarcodeRow): EventViewModel | null {
    for (const ev of this.events) {
      if (ev.apiRows.indexOf(row) !== -1 || ev.extendedRows.indexOf(row) !== -1) {
        return ev;
      }
    }
    return null;
  }

  get gateColWidth(): number {
    const longest = this.gateNames.reduce((max, g) => g.length > max ? g.length : max, 0);
    return Math.max(70, longest * 8 + 16);
  }

  private sanitiseGroups(groups: GateGroup[]): GateGroup[] {
    const seen = new Set<string>();
    const out: GateGroup[] = [];
    for (const g of groups || []) {
      if (!g || !Array.isArray(g.gateNames)) { continue; }
      const filtered = g.gateNames.filter((n) => this.gateNames.includes(n) && !seen.has(n));
      if (!filtered.length) { continue; }
      filtered.forEach((n) => seen.add(n));
      out.push({
        id: g.id || ('g_' + Math.random().toString(36).slice(2, 10)),
        name: g.name || 'Unnamed group',
        gateNames: filtered
      });
    }
    return out;
  }

  groupForGate(gate: string): GateGroup | null {
    for (const g of this.gateGroups) {
      if (g.gateNames.includes(gate)) { return g; }
    }
    return null;
  }

  isGroupCohesiveOnRow(row: BarcodeRow, group: GateGroup): boolean {
    if (!group.gateNames.length) { return true; }
    const first = this.cellExpected(row, group.gateNames[0]);
    for (let i = 1; i < group.gateNames.length; i++) {
      if (this.cellExpected(row, group.gateNames[i]) !== first) { return false; }
    }
    return true;
  }

  rowCellsForRow(_row: BarcodeRow): MatrixColumn[] {
    return this.matrixHeaderColumns();
  }

  slotPxWidth(_col: MatrixColumn): number {
    return Math.max(70, this.gateColWidth);
  }

  matrixColumnsForRow(row: BarcodeRow): MatrixColumn[] {
    return this.matrixHeaderColumns();
  }

  matrixHeaderColumns(): MatrixColumn[] {
    if (this.gateGroupsView !== 'grouped' || !this.gateGroups.length) {
      return this.gateNames.map((g) => ({ kind: 'gate', id: g, label: g, gates: [g] }));
    }
    const out: MatrixColumn[] = [];
    const consumed = new Set<string>();
    for (const g of this.gateNames) {
      if (consumed.has(g)) { continue; }
      const group = this.groupForGate(g);
      if (!group) {
        out.push({ kind: 'gate', id: g, label: g, gates: [g] });
        consumed.add(g);
        continue;
      }

      // User-forced expand: split every member gate individually.
      if (this.expandedGroupIds.has(group.id)) {
        for (const memberGate of group.gateNames) {
          out.push({ kind: 'gate', id: 'expanded:' + group.id + ':' + memberGate, label: memberGate, gates: [memberGate] });
          consumed.add(memberGate);
        }
        continue;
      }

      // Default: split divergent gates out automatically; emit remainder as one group column.
      const divergent = this.groupDivergentGates(group.id);
      if (divergent.length) {
        for (const memberGate of divergent) {
          out.push({ kind: 'gate', id: 'divergent:' + group.id + ':' + memberGate, label: memberGate, gates: [memberGate] });
          consumed.add(memberGate);
        }
        const remainderGates = group.gateNames.filter((n) => !divergent.includes(n));
        if (remainderGates.length) {
          out.push({ kind: 'group', id: group.id, label: group.name, gates: remainderGates });
          remainderGates.forEach((n) => consumed.add(n));
        }
      } else {
        out.push({ kind: 'group', id: group.id, label: group.name, gates: [...group.gateNames] });
        group.gateNames.forEach((n) => consumed.add(n));
      }
    }
    return out;
  }

  isGroupExpanded(groupId: string): boolean {
    return this.expandedGroupIds.has(groupId);
  }

  toggleGroupExpanded(groupId: string) {
    if (this.expandedGroupIds.has(groupId)) {
      this.expandedGroupIds.delete(groupId);
    } else {
      this.expandedGroupIds.add(groupId);
    }
  }

  collapseExpandedGate(colId: string) {
    // colId format: 'expanded:<groupId>:<gateName>'
    if (!colId.startsWith('expanded:')) { return; }
    const rest = colId.slice('expanded:'.length);
    const sep = rest.indexOf(':');
    if (sep < 0) { return; }
    const groupId = rest.slice(0, sep);
    this.expandedGroupIds.delete(groupId);
  }

  groupColumnLabel(col: MatrixColumn): string {
    return col.label;
  }

  groupColumnIsDivergent(_col: MatrixColumn): boolean {
    return false;
  }

  isDivergentSplitColumn(col: MatrixColumn): boolean {
    return col.kind === 'gate' && col.id.indexOf('divergent:') === 0;
  }

  private groupDivergentGates(groupId: string): string[] {
    const group = this.gateGroups.find((g) => g.id === groupId);
    if (!group || group.gateNames.length < 2) { return []; }
    const divergent = new Set<string>();
    for (const ev of this.visibleEvents()) {
      for (const row of this.matrixVisibleRows(ev)) {
        if (this.notScanning(row)) { continue; }
        if (this.isGroupCohesiveOnRow(row, group)) { continue; }
        const counts = new Map<string, string[]>();
        for (const g of group.gateNames) {
          const exp = this.cellExpected(row, g);
          if (!counts.has(exp)) { counts.set(exp, []); }
          counts.get(exp)!.push(g);
        }
        const buckets = Array.from(counts.entries()).sort((a, b) => a[1].length - b[1].length);
        if (buckets.length >= 2) {
          // Mark the minority gates as divergent for THIS row.
          for (const gate of buckets[0][1]) { divergent.add(gate); }
        }
      }
    }
    // Preserve the original gateNames order
    return group.gateNames.filter((g) => divergent.has(g));
  }

  columnState(row: BarcodeRow, col: MatrixColumn): 'accept' | 'reject' | 'mixed' {
    let accepts = 0;
    let rejects = 0;
    for (const g of col.gates) {
      if (this.cellExpected(row, g) === 'Y') { accepts++; } else { rejects++; }
    }
    if (accepts === col.gates.length) { return 'accept'; }
    if (rejects === col.gates.length) { return 'reject'; }
    return 'mixed';
  }

  columnResult(row: BarcodeRow, col: MatrixColumn): 'pass' | 'fail' | 'pending' {
    let anyTested = false;
    for (const g of col.gates) {
      const r = this.cellResult(row, g);
      if (r === 'fail') { return 'fail'; }
      if (r === 'pass') { anyTested = true; }
    }
    return anyTested ? 'pass' : 'pending';
  }

  cellResultMessage(row: BarcodeRow, gate: string): string {
    const entry = row.raw.gateArray?.[gate];
    const history = entry?.resultHistory;
    if (!history || !history.length) { return ''; }
    const latest = history.reduce((a, b) => (Number(b.testTime) > Number(a.testTime) ? b : a));
    return latest?.message ?? '';
  }

  columnTooltip(row: BarcodeRow, col: MatrixColumn): string {
    const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
    const prefix = `Expected Result: "${titleCase(this.columnState(row, col))}"`;

    if (this.currentStage.code !== 'TST') { return prefix; }
    if (this.rowHasActiveRun(row)) { return `${prefix}\nTest: Pending`; }

    const result = this.columnResult(row, col);
    if (result === 'pending') { return prefix; }

    const messages: string[] = [];
    for (const g of col.gates) {
      const msg = this.cellResultMessage(row, g);
      if (!msg) { continue; }
      messages.push(col.gates.length > 1 ? `${g}: "${msg}"` : `"${msg}"`);
    }

    const lines = [prefix, `Test: ${titleCase(result)}`];
    if (messages.length) { lines.push(`Message: ${messages.join('\n')}`); }
    return lines.join('\n');
  }

  columnTrack(_idx: number, col: MatrixColumn) { return col.kind + ':' + col.id; }
  trackEvent(_idx: number, ev: EventViewModel) { return ev.eventName; }
  trackRuleEvent(_idx: number, ev: RuleEvent) { return ev.eventName; }
  trackRow(_idx: number, row: BarcodeRow) { return row.item; }
  trackGate(_idx: number, gate: string) { return gate; }
  trackString(_idx: number, value: string) { return value; }
  trackAuditEntry(_idx: number, entry: AuditEntry) { return entry.time + ':' + entry.action; }

  toggleColumn(row: BarcodeRow, col: MatrixColumn) {
    if (this.saving || this.matrixIsReadOnly || this.notScanning(row)) { return; }
    if (!this.eventForRow(row)?.keep) { return; }

    if (col.kind === 'gate') {
      this.toggleCell(row, col.gates[0]);
      return;
    }
    const state = this.columnState(row, col);
    const next: 'Y' | 'N' = state === 'accept' ? 'N' : 'Y';
    const updatedGateArray = { ...(row.raw.gateArray || {}) };
    for (const g of col.gates) {
      const prev = updatedGateArray[g] || { expected: 'Y', lastResult: '', resultHistory: [] };
      updatedGateArray[g] = { ...prev, expected: next };
    }
    const updated: ScanTestBarcode = {
      ...row.raw,
      item: row.item,
      gateArray: updatedGateArray
    };
    this.saving = true;
    this.apiservice.postScanTest(updated).subscribe({
      next: () => {
        row.raw = updated;
        this.saving = false;
      },
      error: (err) => {
        this.saving = false;
        this.loadError = err?.error?.message ?? 'Failed to update gate group expectation.';
      }
    });
  }

  setGateGroupsView(view: 'grouped' | 'individual') {
    this.gateGroupsView = view;
    this.selectedRowItems.clear();
    this.expandedGroupIds.clear();
  }

  openGateGroupsModal() {
    if (this.matrixIsReadOnly) { return; }
    this.gateGroupsError = '';
    this.showGateGroupsModal = true;
  }

  closeGateGroupsModal() {
    if (this.gateGroupsSaving || this.copyRunning) { return; }
    this.showGateGroupsModal = false;
    this.gateGroupsError = '';
    this.resetCopyState();
  }

  private resetCopyState() {
    this.copyTargets = [];
    this.copyLoading = false;
    this.copyRunning = false;
    this.copyError = '';
    this.copyDone = false;
  }

  onOpenCopy() {
    this.copyError = '';
    this.copyDone = false;
    this.copyLoading = true;
    this.copyTargets = [];

    this.apiservice.getPerformances(this.venueID).subscribe({
      next: (perfs) => {
        const others = (perfs || []).filter((p) => Number(p.item) !== this.performanceTimestamp);
        const targets: CopyTarget[] = others.map((p) => ({
          timestamp: Number(p.item),
          description: p.description || '',
          date: this.formatVenueDate(Number(p.item)) + ' · ' + this.formatVenueTime(Number(p.item)),
          status: 'pending'
        }));
        if (!targets.length) {
          this.copyTargets = [];
          this.copyLoading = false;
          return;
        }
        this.copyTargets = targets;
        this.fetchHasGroupsForTargets(targets);
      },
      error: (err) => {
        this.copyLoading = false;
        this.copyError = err?.error?.message ?? 'Failed to load other performances.';
      }
    });
  }

  private fetchHasGroupsForTargets(targets: CopyTarget[]) {
    const calls = targets.map((t) =>
      this.apiservice.getScanTests(this.venueID, t.timestamp).pipe(
        catchError(() => of<ScanTestRecord[]>([]))
      )
    );
    forkJoin(calls).subscribe({
      next: (results) => {
        results.forEach((records, idx) => {
          const master = (records as ScanTestRecord[]).find((r): r is ScanTestMasterConfig => isMasterConfig(r));
          targets[idx].hasGroups = !!(master?.gateGroups?.length);
        });
        this.copyTargets = [...targets];
        this.copyLoading = false;
      },
      error: () => { this.copyLoading = false; }
    });
  }

  onCloseCopy() {
    if (this.copyRunning) { return; }
    this.resetCopyState();
  }

  onCopyTargets(req: CopyTargetsRequest) {
    if (this.copyRunning || !req.timestamps.length) { return; }
    if (!this.gateGroups.length) {
      this.copyError = 'No groups defined to copy.';
      return;
    }
    if (!req.groupIds || !req.groupIds.length) {
      this.copyError = 'Pick at least one group to copy.';
      return;
    }
    this.copyRunning = true;
    this.copyError = '';
    this.copyDone = false;

    const idSet = new Set(req.groupIds);
    const sourceGroups = this.gateGroups
      .filter((g) => idSet.has(g.id))
      .map((g) => ({
        id: g.id,
        name: g.name,
        gateNames: [...g.gateNames]
      }));

    this.copyTargets = this.copyTargets.map((t) => req.timestamps.includes(t.timestamp)
      ? { ...t, status: 'saving' as const, error: undefined }
      : t);

    let remaining = req.timestamps.length;
    for (const ts of req.timestamps) {
      this.copyOneTarget(ts, sourceGroups, () => {
        remaining -= 1;
        if (remaining === 0) {
          this.copyRunning = false;
          this.copyDone = true;
          const failed = this.copyTargets.filter((t) => t.status === 'error').length;
          if (failed) {
            this.copyError = `Copied to ${req.timestamps.length - failed} of ${req.timestamps.length} performances. ${failed} failed.`;
          }
        }
      });
    }
  }

  private copyOneTarget(timestamp: number, sourceGroups: GateGroup[], done: () => void) {
    const targetKey = buildScanTestKey(this.venueID, timestamp);
    this.apiservice.getScanTests(this.venueID, timestamp).pipe(
      catchError(() => of<ScanTestRecord[]>([]))
    ).subscribe({
      next: (records) => {
        const existingMaster = (records as ScanTestRecord[]).find((r): r is ScanTestMasterConfig => isMasterConfig(r));

        // Discover gate names available at the target by inspecting the first barcode's gateArray.
        const firstBarcode = (records as ScanTestRecord[]).find((r): r is ScanTestBarcode => !isMasterConfig(r));
        const targetGateNames = firstBarcode?.gateArray ? Object.keys(firstBarcode.gateArray) : this.gateNames;
        const targetSet = new Set(targetGateNames);

        const filteredSource: GateGroup[] = sourceGroups
          .map((g) => ({
            id: g.id,
            name: g.name,
            gateNames: g.gateNames.filter((n) => targetSet.has(n))
          }))
          .filter((g) => g.gateNames.length > 0);

        const sourceNames = new Set(filteredSource.map((g) => g.name.toLowerCase()));
        const sourceGates = new Set<string>();
        filteredSource.forEach((g) => g.gateNames.forEach((n) => sourceGates.add(n)));

        const existingGroups: GateGroup[] = existingMaster?.gateGroups ?? [];
        const preservedGroups: GateGroup[] = existingGroups
          .filter((g) => !sourceNames.has((g.name || '').toLowerCase()))
          .map((g) => ({
            id: g.id,
            name: g.name,
            gateNames: (g.gateNames || []).filter((n) => !sourceGates.has(n))
          }))
          .filter((g) => g.gateNames.length > 0);

        const mergedGroups: GateGroup[] = [...preservedGroups, ...filteredSource];

        const nextMaster: ScanTestMasterConfig = {
          key: targetKey,
          item: 0,
          schedule: existingMaster?.schedule ?? [],
          timezone: existingMaster?.timezone ?? (this.venueTimezone || 'Australia/Sydney'),
          notificationEmails: existingMaster?.notificationEmails ?? [],
          gateGroups: mergedGroups
        };

        this.apiservice.postScanTest(nextMaster).subscribe({
          next: () => {
            this.markTargetStatus(timestamp, 'done');
            done();
          },
          error: (err) => {
            this.markTargetStatus(timestamp, 'error', err?.error?.message ?? 'Failed to save');
            done();
          }
        });
      },
      error: (err) => {
        this.markTargetStatus(timestamp, 'error', err?.error?.message ?? 'Failed to load target');
        done();
      }
    });
  }

  private markTargetStatus(timestamp: number, status: CopyTarget['status'], error?: string) {
    this.copyTargets = this.copyTargets.map((t) => t.timestamp === timestamp
      ? { ...t, status, error }
      : t);
  }

  saveGateGroups(updated: GateGroup[]) {
    if (!this.masterRecord) {
      this.gateGroupsError = 'Master config not loaded — please retry shortly.';
      return;
    }
    const sanitised = this.sanitiseGroups(updated);
    const previous = this.gateGroups;
    const previousView = this.gateGroupsView;
    this.gateGroups = sanitised;
    this.gateGroupsView = sanitised.length ? 'grouped' : 'individual';
    this.gateGroupsSaving = true;
    this.gateGroupsError = '';

    const nextMaster: ScanTestMasterConfig = {
      ...this.masterRecord,
      gateGroups: sanitised
    };
    this.apiservice.postScanTest(nextMaster).subscribe({
      next: () => {
        this.masterRecord = nextMaster;
        this.gateGroupsSaving = false;
        this.showGateGroupsModal = false;
      },
      error: (err) => {
        this.gateGroups = previous;
        this.gateGroupsView = previousView;
        this.gateGroupsSaving = false;
        this.gateGroupsError = err?.error?.message ?? 'Failed to save gate groups.';
      }
    });
  }

  // Builds the palette of events and their plans for the Set Rules stage. Only
  // kept events are offered. Cached into ruleEventsList so the template binding
  // receives a stable reference (a fresh array each cycle churns the CDK lists).
  private buildRuleEvents(): RuleEvent[] {
    const out: RuleEvent[] = [];
    for (const ev of this.events) {
      if (!ev.keep || ev.eventName === PerformanceDetailComponent.SEED_EVENT_NAME) { continue; }
      const rows = [...ev.apiRows, ...ev.extendedRows].filter((r) => !this.isEventSeed(r));
      const plans = Array.from(
        new Set(rows.map((r) => (r.plan || '').trim()).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b));
      out.push({ eventName: ev.eventName, description: ev.description || '', plans });
    }
    return out;
  }

  private refreshRuleEvents(): void {
    this.ruleEventsList = this.buildRuleEvents();
  }

  // Loads scan rules from the master, migrating any legacy scan-window rules to
  // the typed EntryRule shape on the way in. Migration is lazy: the converted
  // rules are only persisted back on the next save.
  private migrateAndLoadRules(master: ScanTestMasterConfig | null): GateRule[] {
    const raw = (master?.scanRules ?? []) as Array<GateRule | FlatGateRule | LegacyGateRule>;
    const migrated: GateRule[] = raw.map((rule) => {
      // Legacy scan-window shape → typed grouped shape.
      if (rule && Array.isArray((rule as LegacyGateRule).windows) && !(rule as GateRule).groups) {
        return migrateGateRule(rule as LegacyGateRule);
      }
      // Pre-grouping flat shape (a `rules` array, no `groups`) → single AND group.
      if (rule && Array.isArray((rule as FlatGateRule).rules) && !(rule as GateRule).groups) {
        return liftFlatGateRule(rule as FlatGateRule);
      }
      return rule as GateRule;
    });
    return this.sanitiseRules(migrated);
  }

  // Drops any rule references to events/plans that no longer exist, drops
  // access-group rules whose group is gone, and clamps gates to those
  // configured for this performance. The seed-generating rule types are
  // preserved as-is (their locators aren't validated against live data).
  private sanitiseRules(rules: GateRule[]): GateRule[] {
    const events = this.ruleEventsList;
    const eventNames = new Set(events.map((e) => e.eventName));
    const plansByEvent = new Map(events.map((e) => [e.eventName, new Set(e.plans)]));
    const groupIds = new Set((this.accessGroups || []).map((g) => g.id));

    const validItem = (ref: RuleItemRef): boolean => {
      if (!ref || !eventNames.has(ref.eventName)) { return false; }
      if (ref.kind === 'plan') { return !!ref.plan && !!plansByEvent.get(ref.eventName)?.has(ref.plan); }
      return true;
    };

    const sanitiseRule = (rule: EntryRule): EntryRule | null => {
      switch (rule.type) {
        case 'events': {
          const items = (rule.items || []).filter(validItem);
          return items.length ? { type: 'events', items } : null;
        }
        case 'access_group':
          return groupIds.has(rule.groupId) ? rule : null;
        case 'scan_period':
        case 'section_row':
        case 'ticket_usage':
        case 'price_code':
          return rule;
        default:
          return null;
      }
    };

    const out: GateRule[] = [];
    for (const gateRule of rules || []) {
      if (!gateRule || !this.gateNames.includes(gateRule.gate)) { continue; }
      const groups = (gateRule.groups || [])
        .map((group) => (group || []).map(sanitiseRule).filter((r): r is EntryRule => r !== null))
        .filter((group) => group.length > 0);
      if (groups.length) { out.push({ gate: gateRule.gate, groups }); }
    }
    return out;
  }

  // Restores the Set Rules editor to the last-saved rules (cancel handler).
  reloadScanRules(): void {
    this.scanRules = this.migrateAndLoadRules(this.masterRecord);
    this.rulesError = '';
  }

  // Persists scan rules on the master config, then recomputes and saves each
  // barcode's per-gate expected Y/N so Set Test Config reflects the rules, and
  // seeds any extended test rows requested by locator-based rules.
  saveScanRules(payload: RuleSavePayload): void {
    if (!this.masterRecord) {
      this.rulesError = 'Master config not loaded — please retry shortly.';
      return;
    }
    if (this.rulesSaving) { return; }

    const sanitised = this.sanitiseRules(payload.rules);
    const previous = this.scanRules;
    this.scanRules = sanitised;
    this.rulesSaving = true;
    this.rulesError = '';

    const nextMaster: ScanTestMasterConfig = {
      ...this.masterRecord,
      scanRules: sanitised
    };

    this.apiservice.postScanTest(nextMaster).subscribe({
      next: () => {
        this.masterRecord = nextMaster;
        this.generateSeedRows(payload.seedRequests);
        this.applyRulesToExpected(sanitised);
      },
      error: (err) => {
        this.scanRules = previous;
        this.rulesSaving = false;
        this.rulesError = err?.error?.message ?? 'Failed to save scan rules.';
      }
    });
  }

  // Recomputes expected Y/N for every scanning barcode from the given rules and
  // saves the changed records. Rules on a gate are grouped: within a group the
  // event-gating rules (Events / Access Group) combine with AND — a barcode must
  // satisfy every one; groups combine with OR — any group accepting the barcode
  // accepts it at the gate. Locator rule types (section/row, price code, ticket
  // usage) and scan period don't gate existing barcodes, so they're skipped
  // here; a group with no event-gating rule admits no existing barcode.
  private applyRulesToExpected(rules: GateRule[]): void {
    const groupsById = new Map((this.accessGroups || []).map((g) => [g.id, g]));

    const buildAllow = (items: RuleItemRef[]): Set<string> => {
      const allow = new Set<string>();
      for (const ref of items || []) {
        allow.add(ref.kind === 'plan' ? `plan:${ref.eventName}:${ref.plan}` : `event:${ref.eventName}`);
      }
      return allow;
    };

    // Gate -> array of groups, each an array of the allow-sets its event-gating
    // rules produce. A group with no event-gating rule contributes no allow-set.
    const groupsByGate = new Map<string, Array<Set<string>[]>>();
    for (const gateRule of rules) {
      const gateGroups: Array<Set<string>[]> = [];
      for (const group of gateRule.groups || []) {
        const allowSets: Set<string>[] = [];
        for (const rule of group) {
          if (rule.type === 'events') {
            allowSets.push(buildAllow(rule.items));
          } else if (rule.type === 'access_group') {
            allowSets.push(buildAllow(groupsById.get(rule.groupId)?.items ?? []));
          }
        }
        if (allowSets.length) { gateGroups.push(allowSets); }
      }
      groupsByGate.set(gateRule.gate, gateGroups);
    }

    const matchesAllow = (allow: Set<string>, ev: EventViewModel, row: BarcodeRow): boolean => {
      if (allow.has(`event:${ev.eventName}`)) { return true; }
      const plan = (row.plan || '').trim();
      return !!plan && allow.has(`plan:${ev.eventName}:${plan}`);
    };

    const rowAccepts = (ev: EventViewModel, row: BarcodeRow, gate: string): boolean => {
      const gateGroups = groupsByGate.get(gate);
      if (!gateGroups || !gateGroups.length) { return false; }
      // OR across groups; AND (every allow-set) within a group.
      return gateGroups.some((allowSets) => allowSets.every((allow) => matchesAllow(allow, ev, row)));
    };

    const updates: ScanTestBarcode[] = [];
    for (const ev of this.events) {
      if (!ev.keep) { continue; }
      const rows = [...ev.apiRows, ...ev.extendedRows].filter((r) => !this.isEventSeed(r));
      for (const row of rows) {
        if (this.notScanning(row)) { continue; }
        const gateArray = { ...(row.raw.gateArray || {}) };
        let changed = false;
        for (const gate of this.gateNames) {
          const expected: 'Y' | 'N' = rowAccepts(ev, row, gate) ? 'Y' : 'N';
          const prev = gateArray[gate] || { expected: 'Y', lastResult: '', resultHistory: [] };
          if (prev.expected !== expected) { changed = true; }
          gateArray[gate] = { ...prev, expected };
        }
        if (changed) {
          const updated: ScanTestBarcode = { ...row.raw, item: row.item, gateArray };
          row.raw = updated;
          updates.push(updated);
        }
      }
    }

    if (!updates.length) {
      this.rulesSaving = false;
      return;
    }

    let remaining = updates.length;
    let firstError: any = null;
    for (const record of updates) {
      this.apiservice.postScanTest(record).subscribe({
        next: () => {
          remaining -= 1;
          if (remaining === 0) { this.finishRulesSave(firstError); }
        },
        error: (err) => {
          if (!firstError) { firstError = err; }
          remaining -= 1;
          if (remaining === 0) { this.finishRulesSave(firstError); }
        }
      });
    }
  }

  private finishRulesSave(firstError: any): void {
    this.rulesSaving = false;
    if (firstError) {
      this.rulesError = firstError?.error?.message
        ?? 'Rules saved, but some expected results failed to update.';
    }
  }

  // A stable composite key identifying one seed row: the owning gate, the rule's
  // seedKey, and the concrete locator. Used to avoid duplicating rows on re-save.
  private seedRowKey(gate: string, seedKey: string, hint: SeedRowRequest['locatorHint']): string {
    return [gate, seedKey, hint.section ?? '', hint.row ?? '', hint.priceCode ?? '', hint.ticketUsageLabel ?? '']
      .join('|');
  }

  private existingSeedRowKeys(): Set<string> {
    const keys = new Set<string>();
    for (const ev of this.events) {
      for (const row of ev.extendedRows) {
        const seedKey = row.raw.ruleSeedKey;
        if (!seedKey) { continue; }
        keys.add(this.seedRowKey(
          this.seedGateOf(row) ?? '',
          seedKey,
          { section: row.section, row: row.row, priceCode: row.priceCode, ticketUsageLabel: this.usageLabelOf(row) }
        ));
      }
    }
    return keys;
  }

  // The gate a seed row expects 'Y' at — the single gate its rule owns. Derived
  // from its gateArray so a re-save can rebuild the same composite key.
  private seedGateOf(row: BarcodeRow): string | null {
    const gateArray = row.raw.gateArray || {};
    for (const gate of Object.keys(gateArray)) {
      if (gateArray[gate]?.expected === 'Y') { return gate; }
    }
    return null;
  }

  private usageLabelOf(row: BarcodeRow): string {
    return (row.raw.host_ticket_type || '').trim();
  }

  // Renders a stored ticket-usage label for display. Ticket Usage rules seed the
  // raw "<dayUsage>/<entryUsage>" token (e.g. "single/one_time"); expand it to a
  // readable "1 Day · One-time". Any other value (hand-entered) is shown as-is.
  usageDisplay(row: BarcodeRow): string {
    const label = row.usageLabel?.trim();
    if (!label) { return ''; }
    const parts = label.split('/');
    if (parts.length !== 2) { return label; }
    const dayText = parts[0] === 'single' ? '1 Day' : parts[0] === 'multi' ? 'Multi-Day' : parts[0];
    const entryText = parts[1] === 'one_time' ? 'One-time' : parts[1] === 'unlimited' ? 'Unlimited' : parts[1];
    return `${dayText} · ${entryText}`;
  }

  // Idempotently creates the extended seed rows requested by locator-based rules.
  // Each row is blank-barcode, extended, expected Y only at its owning gate, and
  // carries the rule's seedKey so re-saving the same rules creates no duplicates.
  private generateSeedRows(requests: SeedRowRequest[]): void {
    if (!requests?.length) { return; }
    const existing = this.existingSeedRowKeys();
    let nextItem = this.nextItemNumber();

    for (const req of requests) {
      const composite = this.seedRowKey(req.gate, req.seedKey, req.locatorHint);
      if (existing.has(composite)) { continue; }
      existing.add(composite);

      const gateArray = buildDefaultGateArray(this.gateNames, 'N');
      if (gateArray[req.gate]) {
        gateArray[req.gate] = { ...gateArray[req.gate], expected: 'Y' };
      }

      const item = nextItem;
      nextItem += 1;
      const record: ScanTestBarcode = {
        key: this.scanTestKey,
        item,
        barcode: '',
        gateArray,
        event_name: PerformanceDetailComponent.SEED_EVENT_NAME,
        database_name: '',
        plan_event_name: '',
        fantm_event_name: this.seedRowDescription(req),
        price_code: req.locatorHint.priceCode ?? '',
        section: req.locatorHint.section ?? '',
        row: req.locatorHint.row ?? '',
        seat: '',
        host_ticket_type: req.locatorHint.ticketUsageLabel ?? '',
        extended: true,
        ruleSeedKey: req.seedKey,
        event_type: 'archtics'
      };

      this.apiservice.postScanTest(record).subscribe({
        next: () => this.insertSeedRowIntoVm(record, req),
        error: (err) => {
          this.rulesError = err?.error?.message ?? 'Rules saved, but some seed rows failed to create.';
        }
      });
    }
  }

  private seedRowDescription(req: SeedRowRequest): string {
    switch (req.ruleType) {
      case 'section_row': {
        const section = req.locatorHint.section ?? '';
        return req.locatorHint.row ? `Section ${section}, Row ${req.locatorHint.row}` : `Section ${section}`;
      }
      case 'price_code':
        return `Price code ${req.locatorHint.priceCode ?? ''}`;
      case 'ticket_usage':
        return `Ticket usage ${req.locatorHint.ticketUsageLabel ?? ''}`;
      default:
        return '';
    }
  }

  // Pushes a freshly-created seed record into the VM under the sentinel seed
  // group so testers see it in Set Test Config, creating the group if needed.
  private insertSeedRowIntoVm(record: ScanTestBarcode, req: SeedRowRequest): void {
    const row: BarcodeRow = {
      item: Number(record.item),
      database: '',
      plan: '',
      description: record.fantm_event_name?.trim() || '',
      barcode: '',
      priceCode: record.price_code?.trim() || '',
      section: record.section?.trim() || '',
      row: record.row?.trim() || '',
      seat: '',
      usageLabel: record.host_ticket_type?.trim() || '',
      numSeats: '',
      extended: true,
      eventType: 'archtics',
      raw: record,
      editingBarcode: false,
      barcodeDraft: '',
      barcodeError: false
    };

    let group = this.events.find((ev) => ev.eventName === PerformanceDetailComponent.SEED_EVENT_NAME);
    if (!group) {
      group = {
        eventName: PerformanceDetailComponent.SEED_EVENT_NAME,
        database: '',
        description: 'Test rows seeded from Section & Row, Ticket Usage and Price Code rules',
        eventType: 'archtics',
        apiRows: [],
        extendedRows: [],
        expanded: true,
        matrixExpanded: true,
        keep: true,
        draft: null,
        draftErrors: { db: false, locator: false, barcode: false }
      };
      this.events = [...this.events, group].sort((a, b) => a.eventName.localeCompare(b.eventName));
    }
    group.extendedRows = [...group.extendedRows, row];
  }

  /* Access groups ------------------------------------------------------- */

  // Drops empty/invalid groups and filters each group's items to live
  // events/plans, mirroring how gate groups are sanitised.
  private sanitiseAccessGroups(groups: AccessGroup[]): AccessGroup[] {
    const events = this.ruleEventsList;
    const eventNames = new Set(events.map((e) => e.eventName));
    const plansByEvent = new Map(events.map((e) => [e.eventName, new Set(e.plans)]));

    const validItem = (ref: RuleItemRef): boolean => {
      if (!ref || !eventNames.has(ref.eventName)) { return false; }
      if (ref.kind === 'plan') { return !!ref.plan && !!plansByEvent.get(ref.eventName)?.has(ref.plan); }
      return true;
    };

    const out: AccessGroup[] = [];
    for (const g of groups || []) {
      if (!g) { continue; }
      const items = (g.items || []).filter(validItem);
      if (!items.length) { continue; }
      out.push({
        id: g.id || ('ag_' + Math.random().toString(36).slice(2, 10)),
        name: (g.name || 'Unnamed group').trim(),
        items
      });
    }
    return out;
  }

  // Persists the access groups on the master config, mirroring saveGateGroups.
  saveAccessGroups(updated: AccessGroup[]): void {
    if (!this.masterRecord) {
      this.accessGroupsError = 'Master config not loaded — please retry shortly.';
      return;
    }
    if (this.accessGroupsSaving) { return; }

    const sanitised = this.sanitiseAccessGroups(updated);
    const previous = this.accessGroups;
    this.accessGroups = sanitised;
    this.accessGroupsSaving = true;
    this.accessGroupsError = '';

    const nextMaster: ScanTestMasterConfig = {
      ...this.masterRecord,
      accessGroups: sanitised
    };

    this.apiservice.postScanTest(nextMaster).subscribe({
      next: () => {
        this.masterRecord = nextMaster;
        this.accessGroupsSaving = false;
      },
      error: (err) => {
        this.accessGroups = previous;
        this.accessGroupsSaving = false;
        this.accessGroupsError = err?.error?.message ?? 'Failed to save access groups.';
      }
    });
  }

  // Derives the Price Code suggestion list offered to the rule config modal from
  // every loaded barcode (real and extended, excluding seed shells). Section &
  // Row values are defined by the tester, so they aren't derived here.
  private refreshRuleOptionLists(): void {
    const priceCodes = new Set<string>();

    for (const ev of this.events) {
      for (const row of [...ev.apiRows, ...ev.extendedRows]) {
        if (this.isEventSeed(row)) { continue; }
        const code = (row.priceCode || '').trim();
        if (code) { priceCodes.add(code); }
      }
    }

    this.availablePriceCodes = Array.from(priceCodes)
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
  }

  eventGateState(ev: EventViewModel, gate: string): 'accept' | 'reject' | 'mixed' | 'empty' {
    const targetable = this.matrixVisibleRows(ev).filter((r) => !this.notScanning(r));
    if (!targetable.length) { return 'empty'; }
    let accepts = 0;
    let rejects = 0;
    for (const r of targetable) {
      if (this.cellExpected(r, gate) === 'Y') { accepts++; } else { rejects++; }
    }
    if (accepts === targetable.length) { return 'accept'; }
    if (rejects === targetable.length) { return 'reject'; }
    return 'mixed';
  }

  bulkToggleEventGroup(ev: EventViewModel, col: MatrixColumn) {
    if (this.saving || this.matrixIsReadOnly || !ev.keep) { return; }
    const targetable = this.matrixVisibleRows(ev).filter((r) => !this.notScanning(r));
    if (!targetable.length || !col.gates.length) { return; }

    const allAccept = targetable.every((r) =>
      col.gates.every((g) => this.cellExpected(r, g) === 'Y'));
    const next: 'Y' | 'N' = allAccept ? 'N' : 'Y';

    this.saving = true;
    let remaining = targetable.length;
    let firstError: any = null;

    for (const row of targetable) {
      const gateArray = { ...(row.raw.gateArray || {}) };
      for (const g of col.gates) {
        const prev = gateArray[g] || { expected: 'Y', lastResult: '', resultHistory: [] };
        gateArray[g] = { ...prev, expected: next };
      }
      const updated: ScanTestBarcode = {
        ...row.raw,
        item: row.item,
        gateArray
      };
      this.apiservice.postScanTest(updated).subscribe({
        next: () => {
          row.raw = updated;
          remaining -= 1;
          if (remaining === 0) {
            this.saving = false;
            if (firstError) {
              this.loadError = firstError?.error?.message ?? 'Some rows failed to update.';
            }
          }
        },
        error: (err) => {
          if (!firstError) { firstError = err; }
          remaining -= 1;
          if (remaining === 0) {
            this.saving = false;
            this.loadError = firstError?.error?.message ?? 'Some rows failed to update.';
          }
        }
      });
    }
  }

  bulkToggleEventGate(ev: EventViewModel, gate: string) {
    if (this.saving || this.matrixIsReadOnly || !ev.keep) { return; }
    const targetable = this.matrixVisibleRows(ev).filter((r) => !this.notScanning(r));
    if (!targetable.length) { return; }

    const allAccept = targetable.every((r) => this.cellExpected(r, gate) === 'Y');
    const next: 'Y' | 'N' = allAccept ? 'N' : 'Y';
    const toUpdate = targetable.filter((r) => this.cellExpected(r, gate) !== next);
    if (!toUpdate.length) { return; }

    this.saving = true;
    let remaining = toUpdate.length;
    let firstError: any = null;

    for (const row of toUpdate) {
      const updatedGateArray = { ...(row.raw.gateArray || {}) };
      const previousEntry = updatedGateArray[gate] || { expected: 'Y', lastResult: '', resultHistory: [] };
      updatedGateArray[gate] = { ...previousEntry, expected: next };
      const updated: ScanTestBarcode = {
        ...row.raw,
        item: row.item,
        gateArray: updatedGateArray
      };
      this.apiservice.postScanTest(updated).subscribe({
        next: () => {
          row.raw = updated;
          remaining -= 1;
          if (remaining === 0) {
            this.saving = false;
            if (firstError) {
              this.loadError = firstError?.error?.message ?? 'Some rows failed to update.';
            }
          }
        },
        error: (err) => {
          if (!firstError) { firstError = err; }
          remaining -= 1;
          if (remaining === 0) {
            this.saving = false;
            this.loadError = firstError?.error?.message ?? 'Some rows failed to update.';
          }
        }
      });
    }
  }

  get canSelectRows(): boolean {
    return !this.matrixIsReadOnly || this.currentStage.code === 'TST';
  }

  isRowSelectable(row: BarcodeRow): boolean {
    if (!this.canSelectRows) { return false; }
    if (this.notScanning(row)) { return false; }
    if (this.currentStage.code === 'TST' && !row.barcode) { return false; }
    return true;
  }

  selectableRowsForEvent(ev: EventViewModel): BarcodeRow[] {
    if (!ev.keep) { return []; }
    return this.matrixVisibleRows(ev).filter((r) => this.isRowSelectable(r));
  }

  selectableRowsAll(): BarcodeRow[] {
    const out: BarcodeRow[] = [];
    for (const ev of this.visibleEvents()) {
      out.push(...this.selectableRowsForEvent(ev));
    }
    return out;
  }

  isRowSelected(row: BarcodeRow): boolean {
    return this.selectedRowItems.has(row.item);
  }

  toggleRowSelected(row: BarcodeRow) {
    if (!this.isRowSelectable(row)) { return; }
    if (this.selectedRowItems.has(row.item)) {
      this.selectedRowItems.delete(row.item);
    } else {
      this.selectedRowItems.add(row.item);
    }
  }

  eventSelectState(ev: EventViewModel): 'none' | 'some' | 'all' {
    const selectable = this.selectableRowsForEvent(ev);
    if (!selectable.length) { return 'none'; }
    let selected = 0;
    for (const r of selectable) {
      if (this.selectedRowItems.has(r.item)) { selected++; }
    }
    if (selected === 0) { return 'none'; }
    if (selected === selectable.length) { return 'all'; }
    return 'some';
  }

  toggleEventSelected(ev: EventViewModel) {
    const selectable = this.selectableRowsForEvent(ev);
    if (!selectable.length) { return; }
    if (this.eventSelectState(ev) === 'all') {
      for (const r of selectable) { this.selectedRowItems.delete(r.item); }
    } else {
      for (const r of selectable) { this.selectedRowItems.add(r.item); }
    }
  }

  get selectAllState(): 'none' | 'some' | 'all' {
    const selectable = this.selectableRowsAll();
    if (!selectable.length) { return 'none'; }
    let selected = 0;
    for (const r of selectable) {
      if (this.selectedRowItems.has(r.item)) { selected++; }
    }
    if (selected === 0) { return 'none'; }
    if (selected === selectable.length) { return 'all'; }
    return 'some';
  }

  toggleSelectAll() {
    const selectable = this.selectableRowsAll();
    if (!selectable.length) { return; }
    if (this.selectAllState === 'all') {
      for (const r of selectable) { this.selectedRowItems.delete(r.item); }
    } else {
      for (const r of selectable) { this.selectedRowItems.add(r.item); }
    }
  }

  clearSelection() {
    this.selectedRowItems.clear();
  }

  selectedRowsResolved(): BarcodeRow[] {
    if (!this.selectedRowItems.size) { return []; }
    return this.selectableRowsAll().filter((r) => this.selectedRowItems.has(r.item));
  }

  bulkSetGate(gate: string) {
    this.bulkSetGates([gate]);
  }

  bulkSetGates(gates: string[]) {
    const rows = this.selectedRowsResolved();
    if (!rows.length || !gates.length) { return; }
    const allAccept = rows.every((r) => gates.every((g) => this.cellExpected(r, g) === 'Y'));
    const next: 'Y' | 'N' = allAccept ? 'N' : 'Y';
    this.applyBulkExpectation(rows, (raw) => {
      const gateArray = { ...(raw.gateArray || {}) };
      for (const g of gates) {
        const prev = gateArray[g] || { expected: 'Y', lastResult: '', resultHistory: [] };
        gateArray[g] = { ...prev, expected: next };
      }
      return gateArray;
    });
  }

  bulkSetColumn(col: MatrixColumn) {
    this.bulkSetGates(col.gates);
  }

  bulkColumnState(col: MatrixColumn): 'accept' | 'reject' | 'mixed' | 'empty' {
    const rows = this.selectedRowsResolved();
    if (!rows.length) { return 'empty'; }
    let accepts = 0;
    let rejects = 0;
    const total = rows.length * col.gates.length;
    for (const r of rows) {
      for (const g of col.gates) {
        if (this.cellExpected(r, g) === 'Y') { accepts++; } else { rejects++; }
      }
    }
    if (accepts === total) { return 'accept'; }
    if (rejects === total) { return 'reject'; }
    return 'mixed';
  }

  bulkSetAllGates(value: 'Y' | 'N') {
    const rows = this.selectedRowsResolved();
    if (!rows.length || !this.gateNames.length) { return; }
    this.applyBulkExpectation(rows, (raw) => {
      const gateArray = { ...(raw.gateArray || {}) };
      for (const g of this.gateNames) {
        const prev = gateArray[g] || { expected: 'Y', lastResult: '', resultHistory: [] };
        gateArray[g] = { ...prev, expected: value };
      }
      return gateArray;
    });
  }

  bulkGateState(gate: string): 'accept' | 'reject' | 'mixed' | 'empty' {
    const rows = this.selectedRowsResolved();
    if (!rows.length) { return 'empty'; }
    let accepts = 0;
    let rejects = 0;
    for (const r of rows) {
      if (this.cellExpected(r, gate) === 'Y') { accepts++; } else { rejects++; }
    }
    if (accepts === rows.length) { return 'accept'; }
    if (rejects === rows.length) { return 'reject'; }
    return 'mixed';
  }

  private applyBulkExpectation(
    rows: BarcodeRow[],
    buildGateArray: (raw: ScanTestBarcode) => ScanTestBarcode['gateArray']
  ) {
    if (this.bulkSaving) { return; }
    this.bulkSaving = true;
    this.saving = true;
    let remaining = rows.length;
    let firstError: any = null;

    for (const row of rows) {
      const updated: ScanTestBarcode = {
        ...row.raw,
        item: row.item,
        gateArray: buildGateArray(row.raw)
      };
      this.apiservice.postScanTest(updated).subscribe({
        next: () => {
          row.raw = updated;
          remaining -= 1;
          if (remaining === 0) {
            this.bulkSaving = false;
            this.saving = false;
            if (firstError) {
              this.loadError = firstError?.error?.message ?? 'Some rows failed to update.';
            }
          }
        },
        error: (err) => {
          if (!firstError) { firstError = err; }
          remaining -= 1;
          if (remaining === 0) {
            this.bulkSaving = false;
            this.saving = false;
            this.loadError = firstError?.error?.message ?? 'Some rows failed to update.';
          }
        }
      });
    }
  }

  toggleCell(row: BarcodeRow, gate: string) {
    if (this.saving || this.matrixIsReadOnly || this.notScanning(row)) { return; }
    if (!this.eventForRow(row)?.keep) { return; }
    const current = this.cellExpected(row, gate);
    const next: 'Y' | 'N' = current === 'Y' ? 'N' : 'Y';

    const updatedGateArray = { ...(row.raw.gateArray || {}) };
    const previousEntry = updatedGateArray[gate] || { expected: 'Y', lastResult: '', resultHistory: [] };
    updatedGateArray[gate] = { ...previousEntry, expected: next };

    const updated: ScanTestBarcode = {
      ...row.raw,
      item: row.item,
      gateArray: updatedGateArray
    };

    this.saving = true;
    this.apiservice.postScanTest(updated).subscribe({
      next: () => {
        row.raw = updated;
        this.saving = false;
      },
      error: (err) => {
        this.saving = false;
        this.loadError = err?.error?.message ?? 'Failed to update gate expectation.';
      }
    });
  }

  private nextItemNumber(): number {
    let max = 0;
    for (const ev of this.events) {
      for (const r of ev.apiRows) { if (r.item > max) { max = r.item; } }
      for (const r of ev.extendedRows) { if (r.item > max) { max = r.item; } }
    }
    return max + 1;
  }

  private loadStageOneData() {
    forkJoin({
      gates: this.apiservice.getGates(this.venueID),
      tests: this.apiservice.getScanTests(this.venueID, this.performanceTimestamp).pipe(
        catchError(() => of<ScanTestRecord[]>([]))
      ),
      performances: this.apiservice.getPerformances(this.venueID).pipe(
        catchError(() => of<Performance[]>([]))
      )
    }).subscribe({
      next: ({ gates, tests, performances }) => {
        this.gateNames = this.extractGateNames(gates);
        const records = (tests as ScanTestRecord[]) ?? [];
        const master = records.find((r): r is ScanTestMasterConfig => isMasterConfig(r));
        const barcodes = records
          .filter((r): r is ScanTestBarcode => !isMasterConfig(r))
          .map((r) => ({ ...r, item: Number(r.item) }));

        this.masterRecord = master ?? null;
        this.gateGroups = this.sanitiseGroups(master?.gateGroups ?? []);
        this.gateGroupsView = this.gateGroups.length ? 'grouped' : 'individual';

        this.events = this.groupByEvent(barcodes);
        this.refreshDatabaseFilter();
        this.refreshRuleOptionLists();

        const matched = (performances ?? []).find((p) => Number(p.item) === this.performanceTimestamp);
        if (matched) {
          this.applySavedPerformance(matched);
        } else {
          this.performanceDescription = '';
          this.applyTitle();
          this.performanceStatus = STAGES[0].status;
          this.activeStageIndex = 0;
          this.statusHistory = [];
          this.hiddenEventNames = [];
          this.customArchticsEventNames = [];
          this.customHostEventNames = [];
          this.eventScanMeta = {};
          this.audit = [];
        }
        this.applyHiddenEventsToVm();
        this.accessGroups = this.sanitiseAccessGroups(master?.accessGroups ?? []);
        this.scanRules = this.migrateAndLoadRules(master ?? null);

        if (!master) {
          const newMaster: ScanTestMasterConfig = {
            key: this.scanTestKey,
            item: 0,
            schedule: [],
            timezone: this.venueTimezone || 'Australia/Sydney',
            notificationEmails: []
          };
          this.masterRecord = newMaster;
          this.apiservice.postScanTest(newMaster).subscribe({ error: () => {} });
        }

        this.loading = false;
        this.attachToActiveRun(master);
      },
      error: (err) => {
        this.loading = false;
        this.loadError = err?.error?.message ?? 'Failed to load performance details.';
      }
    });
  }

  private attachToActiveRun(master: ScanTestMasterConfig | undefined) {
    if (this.currentStage.code !== 'TST' || !this.scanTestKey) { return; }

    const cached = master?.currentRun;
    if (cached && (cached.status === 'queued' || cached.status === 'running')) {
      this.currentRun = cached;
      this.testRunning = true;
      this.testRunError = '';
      this.beginPolling(cached.runId);
      return;
    }

    this.apiservice.getScanTestRun(this.scanTestKey).subscribe({
      next: (status) => {
        if (!status?.runId) { return; }
        if (status.status === 'queued' || status.status === 'running') {
          this.currentRun = status;
          this.testRunning = true;
          this.testRunError = '';
          this.beginPolling(status.runId);
        }
      },
      error: () => { /* no active run, or endpoint 404 — silent */ }
    });
  }

  private applySavedPerformance(saved: Performance) {
    this.performanceDescription = saved.description ?? '';
    this.applyTitle();
    this.performanceStatus = saved.status || STAGES[0].status;
    const previousIndex = this.activeStageIndex;
    this.activeStageIndex = this.stageIndexForStatus(this.performanceStatus);
    if (this.activeStageIndex !== previousIndex) {
      this.tm1EditUnlocked = false;
      this.selectedRowItems.clear();
      this.expandedGroupIds.clear();
    }
    this.statusHistory = (saved.status_history ?? []).map((h) => ({
      time: Number(h.time),
      status: h.status,
      username: h.username
    }));
    this.hiddenEventNames = Array.isArray(saved.hiddenEvents) ? [...saved.hiddenEvents] : [];
    this.customArchticsEventNames = Array.isArray(saved.customArchticsEvents) ? [...saved.customArchticsEvents] : [];
    this.customHostEventNames = Array.isArray(saved.customHostEvents) ? [...saved.customHostEvents] : [];
    this.eventScanMeta = this.sanitiseEventScanMeta(saved.eventScanMeta);
    this.audit = (saved.auditTrail ?? [])
      .map((entry) => this.toAuditEntry(entry))
      .sort((a, b) => b.time - a.time);
    this.applyHiddenEventsToVm();
  }

  private applyHiddenEventsToVm() {
    const hidden = new Set(this.hiddenEventNames);
    for (const ev of this.events) {
      ev.keep = !hidden.has(ev.eventName);
    }
    this.refreshRuleEvents();
  }

  private toAuditEntry(entry: PerformanceAuditTrailEntry): AuditEntry {
    const details: string[] = [];
    const changes = entry.changes ?? {};
    const fieldNames = Object.keys(changes);

    for (const field of fieldNames) {
      const change = changes[field];
      if (field === 'hiddenEvents') {
        details.push(...this.describeHiddenEventChange(change.old, change.new));
      } else if (field === 'customArchticsEvents') {
        details.push(...this.describeCustomEventChange(change.old, change.new, 'archtics'));
      } else if (field === 'customHostEvents') {
        details.push(...this.describeCustomEventChange(change.old, change.new, 'host'));
      } else if (field === 'lastExpectedChange') {
        details.push(...this.describeExpectedChange(change.new));
      } else if (field === 'status') {
        details.push(`Status changed from ${this.prettyStatus(change.old)} to ${this.prettyStatus(change.new)}`);
      } else {
        const label = this.fieldLabel(field);
        details.push(`${this.titleCase(label)} changed from ${this.formatAuditValue(change.old)} to ${this.formatAuditValue(change.new)}`);
      }
    }

    let summary: string;
    let variant: 'rollback' | undefined;
    if (entry.action === 'create') {
      summary = 'Performance created';
    } else if (entry.action === 'test_added') {
      const test = entry.test ?? {};
      const itemId = test.item != null ? Number(test.item) : null;
      const matchedRow = itemId != null ? this.findRowByItem(itemId) : null;
      const matchedEvent = matchedRow ? this.eventForRow(matchedRow) : null;
      const typeLabel = matchedEvent?.eventType === 'host' ? 'host' : 'Archtics';
      summary = `Added ${typeLabel} event`;
      if (matchedEvent?.eventName) { details.push(matchedEvent.eventName); }
    } else if (fieldNames.includes('status')) {
      const statusChange = changes['status'];
      const rolledBack = this.isRollback(statusChange.old, statusChange.new);
      summary = rolledBack ? 'Stage rolled back' : 'Stage advanced';
      if (rolledBack) { variant = 'rollback'; }
    } else if (fieldNames.length === 1 && fieldNames[0] === 'hiddenEvents') {
      summary = 'Updated keep / remove selection';
    } else if (fieldNames.length === 1 && fieldNames[0] === 'customArchticsEvents') {
      summary = this.summariseCustomEventChange(changes['customArchticsEvents'].old, changes['customArchticsEvents'].new, 'archtics');
    } else if (fieldNames.length === 1 && fieldNames[0] === 'customHostEvents') {
      summary = this.summariseCustomEventChange(changes['customHostEvents'].old, changes['customHostEvents'].new, 'host');
    } else if (fieldNames.length === 1 && fieldNames[0] === 'lastExpectedChange') {
      const expected = changes['lastExpectedChange'].new as PerformanceExpectedChange | undefined;
      summary = expected?.summary || 'Updated expected result';
    } else {
      const labels = fieldNames.map((f) => this.fieldLabel(f));
      summary = `Updated ${labels.join(', ') || 'record'}`;
    }

    return {
      time: Number(entry.time),
      username: entry.username || '—',
      action: entry.action,
      summary,
      details,
      variant
    };
  }

  private describeHiddenEventChange(oldVal: unknown, newVal: unknown): string[] {
    const oldList = Array.isArray(oldVal) ? oldVal.map((v) => String(v)) : [];
    const newList = Array.isArray(newVal) ? newVal.map((v) => String(v)) : [];
    const oldSet = new Set(oldList);
    const newSet = new Set(newList);
    const lines: string[] = [];
    for (const name of newList) {
      if (!oldSet.has(name)) { lines.push(`${name} marked as REMOVE`); }
    }
    for (const name of oldList) {
      if (!newSet.has(name)) { lines.push(`${name} marked as KEEP`); }
    }
    return lines;
  }

  private describeExpectedChange(newVal: unknown): string[] {
    if (!newVal || typeof newVal !== 'object') { return []; }
    const entries = (newVal as PerformanceExpectedChange).entries;
    return Array.isArray(entries) ? entries.map((s) => String(s)) : [];
  }

  private describeCustomEventChange(oldVal: unknown, newVal: unknown, eventType: EventType): string[] {
    const oldSet = new Set(Array.isArray(oldVal) ? oldVal.map((v) => String(v)) : []);
    const newList = Array.isArray(newVal) ? newVal.map((v) => String(v)) : [];
    const typeLabel = eventType === 'host' ? 'Host' : 'Archtics';
    const lines: string[] = [];
    for (const name of newList) {
      if (!oldSet.has(name)) { lines.push(`${name} added as ${typeLabel} event`); }
    }
    return lines;
  }

  private summariseCustomEventChange(oldVal: unknown, newVal: unknown, eventType: EventType): string {
    const oldSet = new Set(Array.isArray(oldVal) ? oldVal.map((v) => String(v)) : []);
    const added = (Array.isArray(newVal) ? newVal.map((v) => String(v)) : [])
      .filter((n) => !oldSet.has(n));
    const typeLabel = eventType === 'host' ? 'host' : 'Archtics';
    if (added.length === 1) { return `Created ${typeLabel} event "${added[0]}"`; }
    if (added.length > 1) { return `Created ${added.length} ${typeLabel} events`; }
    return `Updated ${typeLabel} event listing`;
  }

  private fieldLabel(field: string): string {
    switch (field) {
      case 'hiddenEvents':          return 'keep / remove';
      case 'customArchticsEvents':  return 'Archtics event listing';
      case 'customHostEvents':      return 'host event listing';
      case 'lastExpectedChange':    return 'expected result';
      case 'description':           return 'description';
      case 'status':                return 'status';
      default:                      return field;
    }
  }

  private isRollback(oldStatus: unknown, newStatus: unknown): boolean {
    if (typeof oldStatus !== 'string' || typeof newStatus !== 'string') { return false; }
    const oldIdx = this.stageIndexForStatus(oldStatus);
    const newIdx = this.stageIndexForStatus(newStatus);
    return newIdx < oldIdx;
  }

  private prettyStatus(value: unknown): string {
    if (value === null || value === undefined || value === '') { return '—'; }
    return this.titleCase(String(value).replace(/_/g, ' '));
  }

  private titleCase(text: string): string {
    return text.replace(/\b\w/g, (c) => c.toUpperCase());
  }

  private formatAuditValue(value: unknown): string {
    if (value === null || value === undefined) { return '—'; }
    if (Array.isArray(value)) {
      if (!value.length) { return 'none'; }
      return value.join(', ');
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) { return '—'; }
      return trimmed;
    }
    return String(value);
  }

  private stageIndexForStatus(status: string): number {
    if (status === 'testing' || status === 'event_day_ready') { return STAGES.length - 1; }
    const idx = STAGES.findIndex((s) => s.status === status);
    return idx >= 0 ? idx : 0;
  }

  get nextStage(): AdvanceStageDescriptor | null {
    if (this.activeStageIndex >= STAGES.length - 1) { return null; }
    return STAGES[this.activeStageIndex + 1];
  }

  get previousStage(): AdvanceStageDescriptor | null {
    if (this.activeStageIndex <= 0) { return null; }
    return STAGES[this.activeStageIndex - 1];
  }

  get canBacktrack(): boolean {
    return (this.isTicketmaster || this.isGlobalAdmin) && !!this.previousStage;
  }

  get currentStage(): AdvanceStageDescriptor {
    return STAGES[this.activeStageIndex];
  }

  get currentStageStatusMeta(): string {
    const last = this.statusHistory.length
      ? this.statusHistory[this.statusHistory.length - 1]
      : null;
    if (last) {
      return `Stage ${this.activeStageIndex + 1} of ${STAGES.length} · entered ${this.formatAuditTime(last.time)} · by ${last.username}`;
    }
    return `Stage ${this.activeStageIndex + 1} of ${STAGES.length} · loaded for ${this.formatAuditTime(this.performanceTimestamp)} · by system (Archtics)`;
  }

  openAdvanceModal() {
    if (this.hasOpenDraft() || this.saving || !this.nextStage) { return; }
    this.advanceError = '';
    this.showAdvanceModal = true;
  }

  cancelAdvance() {
    if (this.advanceSaving) { return; }
    this.showAdvanceModal = false;
    this.advanceError = '';
  }

  confirmAdvance(result: AdvanceStageResult) {
    this.advanceSaving = true;
    this.advanceError = '';
    this.apiservice.postPerformance(this.buildPerformanceBody({ status: result.toStatus })).subscribe({
      next: (saved) => {
        this.applySavedPerformance(saved);
        this.advanceSaving = false;
        this.showAdvanceModal = false;
      },
      error: (err) => {
        this.advanceSaving = false;
        this.advanceError = err?.error?.message ?? 'Failed to advance stage. Please try again.';
      }
    });
  }

  openBackModal() {
    if (!this.canBacktrack || this.hasOpenDraft() || this.saving) { return; }
    this.backError = '';
    this.showBackModal = true;
  }

  cancelBack() {
    if (this.backSaving) { return; }
    this.showBackModal = false;
    this.backError = '';
  }

  confirmBack(result: AdvanceStageResult) {
    this.backSaving = true;
    this.backError = '';
    this.apiservice.postPerformance(this.buildPerformanceBody({ status: result.toStatus })).subscribe({
      next: (saved) => {
        this.applySavedPerformance(saved);
        this.backSaving = false;
        this.showBackModal = false;
      },
      error: (err) => {
        this.backSaving = false;
        this.backError = err?.error?.message ?? 'Failed to roll back stage. Please try again.';
      }
    });
  }

  private deriveEventType(r: ScanTestBarcode): EventType {
    if (r.event_type === 'host') { return 'host'; }
    if ((r.database_name || '').trim().toUpperCase() === 'HOST') { return 'host'; }
    return 'archtics';
  }

  private groupByEvent(records: ScanTestBarcode[]): EventViewModel[] {
    const groups = new Map<string, EventViewModel>();

    for (const r of records) {
      const eventName = r.event_name?.trim() || 'Unknown event';
      const rowType = this.deriveEventType(r);
      let group = groups.get(eventName);
      if (!group) {
        group = {
          eventName,
          database: r.database_name?.trim() || '',
          description: r.description?.trim() || '',
          eventType: rowType,
          apiRows: [],
          extendedRows: [],
          expanded: false,
          matrixExpanded: true,
          keep: true,
          draft: null,
          draftErrors: { db: false, locator: false, barcode: false }
        };
        groups.set(eventName, group);
      } else if (rowType === 'host') {
        group.eventType = 'host';
      }
      const row: BarcodeRow = {
        item: Number(r.item),
        database: r.database_name?.trim() || '',
        plan: r.plan_event_name?.trim() || '',
        description: r.fantm_event_name?.trim() || '',
        barcode: r.barcode?.trim() || '',
        priceCode: r.price_code?.trim() || '',
        section: r.section?.trim() || '',
        row: r.row?.trim() || '',
        seat: r.seat?.trim() || '',
        usageLabel: r.host_ticket_type?.trim() || '',
        numSeats: typeof r.num_seats === 'number' ? String(r.num_seats) : (r.num_seats ?? ''),
        extended: r.extended === true,
        eventType: rowType,
        raw: r,
        editingBarcode: false,
        barcodeDraft: '',
        barcodeError: false
      };
      if (row.extended) {
        group.extendedRows.push(row);
      } else {
        group.apiRows.push(row);
      }
    }

    return Array.from(groups.values()).sort((a, b) => a.eventName.localeCompare(b.eventName));
  }

  private extractGateNames(gatesResponse: any): string[] {
    const names = this.collectGateNames(gatesResponse);
    return names.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
  }

  private collectGateNames(gatesResponse: any): string[] {
    if (!gatesResponse) { return []; }
    if (Array.isArray(gatesResponse)) {
      return gatesResponse
        .map((g: any) => (typeof g === 'string' ? g : (g?.name ?? g?.gate ?? '')))
        .filter((s: string) => typeof s === 'string' && s.length > 0);
    }
    if (gatesResponse && typeof gatesResponse === 'object') {
      if (Array.isArray(gatesResponse.gates)) {
        return this.collectGateNames(gatesResponse.gates);
      }
      const keys = Object.keys(gatesResponse);
      const objectLooksLikeMap = keys.every((k) => typeof gatesResponse[k] === 'object');
      if (objectLooksLikeMap) { return keys; }
    }
    return [];
  }

  private applyTitle() {
    const description = (this.performanceDescription || '').trim();
    this.performanceTitle = description || `Performance · ${this.venueName}`;
    this.performanceSubtitle = description
      ? `${this.formatVenueDate(this.performanceTimestamp)} · ${this.venueName} · ${this.formatVenueTime(this.performanceTimestamp)}`
      : `${this.formatVenueDate(this.performanceTimestamp)} · ${this.formatVenueTime(this.performanceTimestamp)}`;
  }

  private epochToVenueDate(epochSeconds: number): string {
    const tz = this.venueTimezone || 'UTC';
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(new Date(epochSeconds * 1000));
    const lookup: Record<string, string> = {};
    parts.forEach((p) => { lookup[p.type] = p.value; });
    return `${lookup['year']}-${lookup['month']}-${lookup['day']}`;
  }

  private formatVenueDate(epochSeconds: number): string {
    return new Intl.DateTimeFormat('en-AU', {
      timeZone: this.venueTimezone || 'UTC',
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(new Date(epochSeconds * 1000));
  }

  private formatVenueTime(epochSeconds: number): string {
    return new Intl.DateTimeFormat('en-AU', {
      timeZone: this.venueTimezone || 'UTC',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(new Date(epochSeconds * 1000));
  }
}
